/* =========================================================================
   Sticker Sheet Maker — assets/sticker-sheet-maker.js (bearly.art)
   Runs on the Bearly Tools shell. Needs clipper-lib + JSZip (loaded by the section).
   GA4: pushes ss_* events to window.dataLayer; the shared core
   (assets/bearly-tools-core.js) forwards them to GA4 and fires ss_tool_view.
   Download popup + email gate: the ONE shared popup in the core (BT.runWithProgress):
   progress (this tool's stage texts), email step only if not unlocked, then the file.
   The core owns the popup, gate, × / Escape / backdrop close, Klaviyo subscribe, unlock
   keys and the ss_email_* / ss_progress_cancel / ss_export_error events. Old core
   cached (no runWithProgress): the file is made straight away.
   Everything runs in the browser; images never leave the device.
   Sample stickers: #ss-samples loads the Shopify Files listed in #ss-sample-data
   through addFiles(files,'sample'); ss_file_upload carries source = file | sample | library.
   Sample sheet on load (Oct 7 2026): first visits open on a finished sheet — the same samples,
   loaded through addFiles(files,'demo') (no ss_file_upload / Started / stickers_placed), placed and
   then filled with copies. #ss-demoBar offers "Download this sheet" (normal popup + gate,
   ss_demo_download_click) and "Use my own images". Any upload, library pick, Clear sheet or that
   button drops the samples (ss_demo_replaced) and sets localStorage ss_seen so return visits open
   blank. ?demo=0 forces blank, ?demo=1 forces the sample sheet. ss_demo_loaded fires when it's up.
   Uploads go straight onto the sheet: every new sticker is placed as soon as its image is read.
   Background removal (cutMask) scores its own result. Photos, busy or patterned backgrounds and
   anything it can't cut cleanly are kept WHOLE: one sticker cropped to a shape (rounded by default;
   circle, oval, square, heart, star or the full image). "Background: Remove / Keep" in step 1 flips
   every image; the top bar's Shape menu ("Cut out" or a shape) flips one image.
   Free designs (#ss-svg): a bare picker over the SVG Library's data in Shopify Files
   (svglib-index.json + its packs, same files and format as assets/svg-library.js). A picked
   SVG is drawn to a transparent PNG here and goes through the normal upload path.
   ========================================================================= */
(() => {
'use strict';
const ROOT=document.getElementById('ss-root'); if(!ROOT) return;
const ACC=(getComputedStyle(ROOT).getPropertyValue('--bt-accent')||'').trim()||'#A8622A';
// Biggest canvas the browser can make. iPhone/iPad Safari stops at 16.7 MP and draws nothing past it.
const IOS=/iP(hone|ad|od)/.test(navigator.userAgent)||(navigator.platform==='MacIntel'&&navigator.maxTouchPoints>1);
const MAX_PX=IOS?16777216:12e7;
function fitDpi(wIn,hIn,dpi){ for(const d of [600,300,150]) if(d<=dpi&&Math.round(wIn*d)*Math.round(hIn*d)<=MAX_PX) return d; return Math.min(dpi,150); }
const ACC_A=a=>{ const m=/^#([0-9a-f]{6})$/i.exec(ACC); if(!m) return `rgba(168,98,42,${a})`; const n=parseInt(m[1],16); return `rgba(${n>>16&255},${n>>8&255},${n&255},${a})`; };
const $ = s => document.querySelector(s);
const PREV = 100;          // preview pixels per inch
const GRID = 32;           // nesting cells per inch
const INF = 1e20;
let uid = 1;
const nid = () => uid++;

/* ---------------- tracking + Klaviyo (standard Bearly Tools pattern) ---------------- */
const TOOL={ name:'Sticker Sheet Maker', handle:'sticker-sheet-maker' };
function track(name,params){ const o=params?JSON.parse(JSON.stringify(params)):{}; o.event=name; (window.dataLayer=window.dataLayer||[]).push(o); }
/* Klaviyo per-tool events: the core sends them for the known email (adds tool + tool_handle) */
function klEvent(name,props){ try{ if(window.BT&&typeof window.BT.klaviyoEvent==='function') window.BT.klaviyoEvent(name,props); }catch(e){} }
let startedSent=false, placedSent=false;
function notePlaced(how){ if(placedSent) return; placedSent=true; track('ss_stickers_placed',{ how, stickers_found:S.stickers.size }); }

/* ---------------- state ---------------- */
const S = {
  sources: [],          // {id,name,url,data:ImageData,w,h}
  stickers: new Map(),  // id -> {id,srcId,name,canvas,w,h,bbox,natW}
  sheets: [{id:nid(), items:[]}],
  cur: 0,
  sel: null,            // item id (one sticker selected)
  multi: new Set(),     // item ids when 2+ are selected (box select / Shift+click); sel is null then
  mode: 'paper',
  offset: 0.06, offColor:'#ffffff', smooth:0.06, showCut:false,
  lineOn: true, lineW: 0.01, lineC:'#2a2521', whiteBg:false,
  sheetW: 8.5, sheetH: 11, margin: 0.1, gap: 0.06, allowRot: true,
  dpi: 300, trim: true,
  det: {sens:1, big:3},   // cut-out strength (1 = auto), largest sticker per image (in)
  demo: false,            // sample sheet showing (see startDemo / exitDemo)
};
const undoStack = [];
const sheet = () => S.sheets[S.cur];
const findItem = id => sheet().items.find(i => i.id === id);
// the selected items: one (S.sel) or several (S.multi)
const selItems = () => S.multi.size ? sheet().items.filter(i => S.multi.has(i.id)) : (findItem(S.sel) ? [findItem(S.sel)] : []);
function setSelection(ids){ S.multi=new Set(ids); S.sel=null; if(S.multi.size===1){ S.sel=[...S.multi][0]; S.multi.clear(); } }

const grave={stickers:new Map(), sources:new Map()};   // deleted items kept so Undo can bring them back
function snapshot(){
  undoStack.push(JSON.stringify({sheets:S.sheets, cur:S.cur, stickers:[...S.stickers.keys()], sources:S.sources.map(x=>x.id)}));
  if (undoStack.length > 60) undoStack.shift();
}
function undo(){
  const s = undoStack.pop(); if (!s) return toast('Nothing to undo');
  const o = JSON.parse(s);
  if(o.sources){ for(const id of o.sources){ if(!S.sources.some(x=>x.id===id) && grave.sources.has(id)){ const g=grave.sources.get(id); S.sources.splice(Math.min(g.idx,S.sources.length),0,g.src); grave.sources.delete(id); } } }
  if(o.stickers){ const back=o.stickers.filter(id=>!S.stickers.has(id)&&grave.stickers.has(id));
    const keep=new Set(o.stickers), extra=[...S.stickers.values()].filter(st=>st.fromSplit&&!keep.has(st.id));
    if(back.length||extra.length){ const m=new Map(); for(const id of o.stickers){ const st=S.stickers.get(id)||grave.stickers.get(id); if(st) m.set(id,st); grave.stickers.delete(id); }
      for(const [id,st] of S.stickers) if(!m.has(id)){ if(st.fromSplit&&!keep.has(id)) grave.stickers.set(id,st); else m.set(id,st); } S.stickers=m; } }
  S.sheets = o.sheets.map(sh => ({...sh, items: sh.items.filter(i => S.stickers.has(i.stickerId))}));
  S.cur = Math.min(o.cur, S.sheets.length-1); S.sel = null; S.multi.clear();
  renderSources(); refresh();
}

/* ---------------- utils ---------------- */
let toastT;
function toast(msg, ms=2600){ const t=$('#ss-toast'); t.textContent=msg; t.classList.add('is-on'); clearTimeout(toastT); toastT=setTimeout(()=>t.classList.remove('is-on'),ms); }
const clamp=(v,a,b)=>v<a?a:v>b?b:v;
const fmt=v=>(Math.round(v*100)/100).toFixed(2);
function mk(w,h){ const c=document.createElement('canvas'); c.width=Math.max(1,w|0); c.height=Math.max(1,h|0); return c; }
function hexRGB(h){ const n=parseInt(h.slice(1),16); return [n>>16&255,n>>8&255,n&255]; }
const tick=()=>new Promise(r=>setTimeout(r,0));

/* Felzenszwalb squared Euclidean distance transform, in place on Float32Array */
function edt(g,w,h){
  const n=Math.max(w,h), f=new Float32Array(n), d=new Float32Array(n), v=new Int32Array(n), z=new Float32Array(n+1);
  const pass=(len)=>{
    let k=0; v[0]=0; z[0]=-INF; z[1]=INF;
    for(let q=1;q<len;q++){
      let s=((f[q]+q*q)-(f[v[k]]+v[k]*v[k]))/(2*q-2*v[k]);
      while(s<=z[k]){ k--; s=((f[q]+q*q)-(f[v[k]]+v[k]*v[k]))/(2*q-2*v[k]); }
      k++; v[k]=q; z[k]=s; z[k+1]=INF;
    }
    k=0;
    for(let q=0;q<len;q++){ while(z[k+1]<q) k++; const r=q-v[k]; d[q]=r*r+f[v[k]]; }
  };
  for(let x=0;x<w;x++){ for(let y=0;y<h;y++) f[y]=g[y*w+x]; pass(h); for(let y=0;y<h;y++) g[y*w+x]=d[y]; }
  for(let y=0;y<h;y++){ const o=y*w; for(let x=0;x<w;x++) f[x]=g[o+x]; pass(w); for(let x=0;x<w;x++) g[o+x]=d[x]; }
  return g;
}
/* squared distance to nearest set pixel */
function distTo(mask,w,h){ const g=new Float32Array(w*h); for(let i=0;i<g.length;i++) g[i]=mask[i]?0:INF; return edt(g,w,h); }
/* squared distance (inside set pixels) to nearest unset pixel; canvas edge counts as unset */
function depthIn(mask,w,h){
  const W=w+2,H=h+2,g=new Float32Array(W*H);
  for(let y=0;y<h;y++) for(let x=0;x<w;x++) g[(y+1)*W+x+1]=mask[y*w+x]?INF:0;
  edt(g,W,H);
  const o=new Float32Array(w*h); for(let y=0;y<h;y++) for(let x=0;x<w;x++) o[y*w+x]=g[(y+1)*W+x+1]; return o;
}
/* fill enclosed holes of a binary mask (in place) */
function fillHoles(m,w,h){
  const seen=new Uint8Array(w*h), st=new Int32Array(w*h); let sp=0;
  const push=i=>{ if(!m[i]&&!seen[i]){ seen[i]=1; st[sp++]=i; } };
  for(let x=0;x<w;x++){ push(x); push((h-1)*w+x); }
  for(let y=0;y<h;y++){ push(y*w); push(y*w+w-1); }
  while(sp){ const i=st[--sp], x=i%w, y=(i/w)|0;
    if(x>0)push(i-1); if(x<w-1)push(i+1); if(y>0)push(i-w); if(y<h-1)push(i+w); }
  for(let i=0;i<m.length;i++) if(!m[i]&&!seen[i]) m[i]=1;
  return m;
}

/* ---------------- manual split ----------------
   Draw straight cut lines across a merged sticker; pixels under the lines are removed, the rest is
   split into connected pieces (small crumbs and the cut pixels are given back to the nearest piece). */
const SP={st:null,lines:[],cur:null,labels:null,n:0,view:null,mask:null};
function openSplit(stId){
  const st=S.stickers.get(stId); if(!st) return;
  const a=st.canvas.getContext('2d',{willReadFrequently:true}).getImageData(0,0,st.w,st.h).data, m=new Uint8Array(st.w*st.h);
  for(let i=0;i<m.length;i++) m[i]=a[i*4+3]>20?1:0;
  Object.assign(SP,{st,lines:[],cur:null,mask:m});
  $('#ss-split').hidden=false; splitCompute(); requestAnimationFrame(splitDraw);
}
function closeSplit(){ $('#ss-split').hidden=true; SP.st=null; }
function splitCompute(){
  const {st,mask}=SP, w=st.w, h=st.h, N=w*h, cut=new Uint8Array(N), lw=Math.max(1.5,Math.min(w,h)/250);
  for(const L of SP.lines){ // rasterise each cut line as a thick segment
    const [x0,y0,x1,y1]=L, len=Math.hypot(x1-x0,y1-y0)||1, steps=Math.ceil(len*2);
    for(let k=0;k<=steps;k++){ const cx=x0+(x1-x0)*k/steps, cy=y0+(y1-y0)*k/steps, r=Math.ceil(lw);
      for(let dy=-r;dy<=r;dy++) for(let dx=-r;dx<=r;dx++){ if(dx*dx+dy*dy>lw*lw) continue; const x=Math.round(cx+dx), y=Math.round(cy+dy); if(x>=0&&y>=0&&x<w&&y<h) cut[y*w+x]=1; } }
  }
  const lab=new Int32Array(N), st2=new Int32Array(N), sizes=[0]; let n=0, total=0;
  for(let i=0;i<N;i++) if(mask[i]) total++;
  for(let s0=0;s0<N;s0++){ if(!mask[s0]||cut[s0]||lab[s0]) continue;
    n++; let sp=0, a=0; st2[sp++]=s0; lab[s0]=n;
    while(sp){ const i=st2[--sp]; a++; const x=i%w;
      for(const j of [x>0?i-1:-1, x<w-1?i+1:-1, i-w, i+w]){ if(j<0||j>=N||!mask[j]||cut[j]||lab[j]) continue; lab[j]=n; st2[sp++]=j; } }
    sizes.push(a); }
  // keep real pieces; crumbs + cut pixels get absorbed by the nearest real piece (multi-source BFS)
  const min=Math.max(30,total*0.004), keep=new Int32Array(n+1); let k=0;
  for(let l=1;l<=n;l++) if(sizes[l]>=min) keep[l]=++k;
  const out=new Int32Array(N), q=new Int32Array(N); let qh=0,qt=0;
  for(let i=0;i<N;i++){ if(lab[i]&&keep[lab[i]]){ out[i]=keep[lab[i]]; q[qt++]=i; } }
  while(qh<qt){ const i=q[qh++], x=i%w;
    for(const j of [x>0?i-1:-1, x<w-1?i+1:-1, i-w, i+w]){ if(j<0||j>=N||!mask[j]||out[j]) continue; out[j]=out[i]; q[qt++]=j; } }
  SP.labels=out; SP.n=k;
  $('#ss-spCount').textContent=k>1?`${k} pieces`:'1 piece — draw a line across a gap';
  $('#ss-spApply').disabled=k<2;
}
const SP_COLS=[[230,57,70],[42,157,143],[69,123,157],[244,162,97],[131,56,236],[233,196,106],[38,70,83],[255,0,110],[58,134,255],[106,153,78]];
function splitDraw(){
  if(!SP.st) return;
  const cv=$('#ss-spCv'), box=$('#ss-spStage').getBoundingClientRect(), dpr=window.devicePixelRatio||1;
  cv.width=Math.round(box.width*dpr); cv.height=Math.round(box.height*dpr);
  const x=cv.getContext('2d'); x.setTransform(dpr,0,0,dpr,0,0);
  const {st}=SP, pad=20, z=Math.min((box.width-pad*2)/st.w,(box.height-pad*2)/st.h), ox=(box.width-st.w*z)/2, oy=(box.height-st.h*z)/2;
  SP.view={z,ox,oy};
  x.imageSmoothingQuality='high'; x.drawImage(st.canvas,ox,oy,st.w*z,st.h*z);
  if(SP.n>1){ // colour wash per piece
    const t=mk(st.w,st.h), tx=t.getContext('2d'), id=tx.createImageData(st.w,st.h), d=id.data;
    for(let i=0;i<SP.labels.length;i++){ const l=SP.labels[i]; if(!l) continue; const c=SP_COLS[(l-1)%SP_COLS.length]; d[i*4]=c[0]; d[i*4+1]=c[1]; d[i*4+2]=c[2]; d[i*4+3]=110; }
    tx.putImageData(id,0,0); x.drawImage(t,ox,oy,st.w*z,st.h*z);
  }
  x.lineCap='round'; x.strokeStyle='#C0392B'; x.lineWidth=2.5; x.setLineDash([]);
  for(const L of SP.lines.concat(SP.cur?[SP.cur]:[])){ x.beginPath(); x.moveTo(ox+L[0]*z,oy+L[1]*z); x.lineTo(ox+L[2]*z,oy+L[3]*z); x.stroke(); }
}
(function(){
  const stg=$('#ss-spStage');
  const toSt=e=>{ const r=stg.getBoundingClientRect(), v=SP.view; return [(e.clientX-r.left-v.ox)/v.z,(e.clientY-r.top-v.oy)/v.z]; };
  stg.addEventListener('pointerdown',e=>{ if(!SP.st) return; stg.setPointerCapture(e.pointerId); const p=toSt(e); SP.cur=[p[0],p[1],p[0],p[1]]; });
  stg.addEventListener('pointermove',e=>{ if(!SP.cur) return; const p=toSt(e); SP.cur[2]=p[0]; SP.cur[3]=p[1]; splitDraw(); });
  const up=()=>{ if(!SP.cur) return; const L=SP.cur; SP.cur=null; if(Math.hypot(L[2]-L[0],L[3]-L[1])>3){ SP.lines.push(L); splitCompute(); } splitDraw(); };
  stg.addEventListener('pointerup',up); stg.addEventListener('pointercancel',up);
  $('#ss-spUndo').onclick=()=>{ SP.lines.pop(); splitCompute(); splitDraw(); };
  $('#ss-spCancel').onclick=closeSplit;
  $('#ss-split').addEventListener('pointerdown',e=>{ if(e.target.id==='ss-split') closeSplit(); });
  window.addEventListener('keydown',e=>{ if(!SP.st) return; if(e.key==='Escape'){ e.stopImmediatePropagation(); closeSplit(); } if((e.ctrlKey||e.metaKey)&&e.key.toLowerCase()==='z'){ e.preventDefault(); e.stopImmediatePropagation(); $('#ss-spUndo').click(); } },true);
  window.addEventListener('resize',()=>SP.st&&splitDraw());
  $('#ss-spApply').onclick=applySplit;
})();
function applySplit(){
  const {st,labels,n}=SP; if(!st||n<2) return;
  snapshot();
  const w=st.w, h=st.h, src=st.canvas.getContext('2d',{willReadFrequently:true}).getImageData(0,0,w,h).data, pieces=[];
  for(let l=1;l<=n;l++){
    let x0=w,y0=h,x1=-1,y1=-1; for(let y=0;y<h;y++) for(let x=0;x<w;x++) if(labels[y*w+x]===l){ if(x<x0)x0=x; if(x>x1)x1=x; if(y<y0)y0=y; if(y>y1)y1=y; }
    if(x1<0) continue;
    const P=2, cw=x1-x0+1+P*2, ch=y1-y0+1+P*2, c=mk(cw,ch), cx=c.getContext('2d',{willReadFrequently:true}), id=cx.createImageData(cw,ch), d=id.data;
    for(let y=y0;y<=y1;y++) for(let x=x0;x<=x1;x++){ const i=y*w+x; if(labels[i]!==l) continue; const q=((y-y0+P)*cw+(x-x0+P))*4, o=i*4; d[q]=src[o]; d[q+1]=src[o+1]; d[q+2]=src[o+2]; d[q+3]=src[o+3]; }
    cx.putImageData(id,0,0);
    const sx=st.bbox[2]/Math.max(1,w-4), bb=[st.bbox[0]+(x0-P-2)*sx, st.bbox[1]+(y0-P-2)*sx, cw*sx, ch*sx];
    pieces.push({st:{id:nid(),srcId:st.srcId,name:`${st.name}${String.fromCharCode(96+l)}`,canvas:c,w:cw,h:ch,bbox:bb,natW:st.natW*cw/w,thumb:null,fromSplit:true},
                 cx:(x0-P)+cw/2-w/2, cy:(y0-P)+ch/2-h/2});
  }
  // swap the original for its pieces in the list, keeping its position
  const m=new Map(); for(const [id,s0] of S.stickers){ if(id===st.id){ for(const p of pieces) m.set(p.st.id,p.st); } else m.set(id,s0); }
  grave.stickers.set(st.id,st); S.stickers=m;
  // placed copies → pieces in the same spot, same scale and rotation
  let moved=0;
  for(const sh of S.sheets){ const out=[];
    for(const it of sh.items){
      if(it.stickerId!==st.id){ out.push(it); continue; }
      const k=it.widthIn/w, a=it.rot*Math.PI/180, co=Math.cos(a), si=Math.sin(a);
      for(const p of pieces) out.push({...it,id:nid(),stickerId:p.st.id,widthIn:it.widthIn*p.st.w/w,x:it.x+(p.cx*co-p.cy*si)*k,y:it.y+(p.cx*si+p.cy*co)*k});
      moved++;
    }
    sh.items=out; }
  S.sel=null; closeSplit(); renderSources(); renderLib(); refresh();
  track('ss_split_apply',{ pieces:pieces.length, placed_updated:moved });
  toast(`Split into ${pieces.length} stickers`+(moved?` · ${moved} placed cop${moved>1?'ies':'y'} updated`:''));
}

/* ---------------- frame detection ----------------
   Some sheet images have a thin line/box around the whole thing. Scan in from each edge: if we meet a
   thin band of near-uniform, non-background rows (the frame line) followed by clean background, crop
   just inside it. Only thin bands count, so stickers touching the edge are never cropped. */
let framesRemoved=0, loading=0;
function findFrame(img){
  const {data:px,width:w,height:h}=img;
  if(w<60||h<60) return null;
  let clear=0; for(let i=3;i<px.length;i+=64) if(px[i]<24) clear++; if(clear>px.length/64*0.02) return null; // transparent input: no frame
  // background = median of a ring ~4% in from the edge (inside any frame)
  const rs=[],gs=[],bs=[], ix=Math.round(w*0.04), iy=Math.round(h*0.04);
  const samp=(x,y)=>{ const o=(y*w+x)*4; rs.push(px[o]); gs.push(px[o+1]); bs.push(px[o+2]); };
  for(let x=ix;x<w-ix;x+=3){ samp(x,iy); samp(x,h-1-iy); } for(let y=iy;y<h-iy;y+=3){ samp(ix,y); samp(w-1-ix,y); }
  const med=a=>a.sort((p,q)=>p-q)[a.length>>1], br=med(rs), bg=med(gs), bb=med(bs);
  const isBg=o=>{ const r=px[o]-br,g=px[o+1]-bg,b=px[o+2]-bb; return r*r+g*g+b*b<=30*30*3; };
  // line stats: fraction of background pixels, and fraction matching the line's own median colour
  const lineStat=(horiz,k)=>{ const n=horiz?w:h, lo=Math.round(n*0.08), hi=n-lo; let bgc=0,cnt=0; const R=[],G=[],B=[];
    for(let t=lo;t<hi;t+=2){ const o=horiz?(k*w+t)*4:(t*w+k)*4; cnt++; if(isBg(o)) bgc++; R.push(px[o]);G.push(px[o+1]);B.push(px[o+2]); }
    const mr=med(R.slice()),mg=med(G.slice()),mb=med(B.slice()); let same=0;
    for(let i=0;i<R.length;i++){ const r=R[i]-mr,g=G[i]-mg,b=B[i]-mb; if(r*r+g*g+b*b<=40*40*3) same++; }
    return {bg:bgc/cnt, uni:same/R.length}; };
  const side=(horiz,fromEnd)=>{
    const n=horiz?h:w, maxScan=Math.max(6,Math.round(n*0.06)), maxBand=Math.max(3,Math.round(n*0.02));
    let inBand=false, bandLen=0, lastBand=-1;
    for(let d=0; d<maxScan; d++){
      const k=fromEnd?n-1-d:d, st=lineStat(horiz,k);
      const frameLine = st.bg<0.5 && st.uni>0.7;
      if(frameLine){ inBand=true; bandLen++; lastBand=d; if(bandLen>maxBand) return 0; }
      else if(st.bg>0.9){ if(inBand) return lastBand+2; }      // clean background after a thin band → frame found
      else if(inBand) return 0;                                  // band followed by artwork → not a frame
      else if(st.bg<0.9 && !frameLine && d>1) return 0;          // artwork right at the edge
    }
    return 0;
  };
  const t=side(true,false), b=side(true,true), l=side(false,false), r=side(false,true);
  if(t+b+l+r===0) return null;
  // a real frame goes all the way round; require at least 3 sides so an edge-touching sticker can't trigger it
  if([t,b,l,r].filter(v=>v>0).length<3) return null;
  return {x:l, y:t, w:w-l-r, h:h-t-b};
}

/* ---------------- loading sources ---------------- */
const MAXDIM = 2400;   // 8 in at 300 DPI — plenty for stickers, and keeps big uploads fast
async function addFiles(files, source='file'){
  const imgs=[...files].filter(f=>f.type.startsWith('image/'));
  if(!imgs.length) return toast('Those files are not images');
  const demo=source==='demo';
  if(!demo) exitDemo(source==='library'?'library':'upload');   // the user's own images replace the sample sheet
  const before=new Set(S.stickers.keys()); loading++; let photosAdded=0;
  if(!demo) toast(`Finding stickers in ${imgs.length} image${imgs.length>1?'s':''}…`, 60000);
  for(const [fi,f] of imgs.entries()){
    if(demo&&!S.demo) break;                                     // sample sheet dropped while it was loading
    if(!demo&&imgs.length>1) toast(`Finding stickers… image ${fi+1} of ${imgs.length}`, 60000);
    await tick();
    try{
      const url=URL.createObjectURL(f);
      const img=await new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=url; });
      const sc=Math.min(1, MAXDIM/Math.max(img.naturalWidth,img.naturalHeight));
      const c=mk(Math.round(img.naturalWidth*sc), Math.round(img.naturalHeight*sc));
      let x=c.getContext('2d',{willReadFrequently:true}); x.imageSmoothingQuality='high'; x.drawImage(img,0,0,c.width,c.height);
      const full=x.getImageData(0,0,c.width,c.height);
      // a screenshot of a transparent image (grey/white checkerboard baked in): make the checkerboard see-through
      if(imageKind(full)!=='transparent'&&knockOutChecker(full)) x.putImageData(full,0,0);
      const transparent=imageKind(full)==='transparent';
      let cc=c; const fr=findFrame(full);
      if(fr){ cc=mk(fr.w,fr.h); x=cc.getContext('2d',{willReadFrequently:true}); x.drawImage(c,fr.x,fr.y,fr.w,fr.h,0,0,fr.w,fr.h); framesRemoved++; }
      const src={id:nid(), name:f.name.replace(/\.[^.]+$/,''), url, data:fr?x.getImageData(0,0,cc.width,cc.height):full, w:cc.width, h:cc.height, canvas:cc,
        opaque:!transparent, photo:false, demo};
      S.sources.push(src);
      await tick();
      // cut it out; if the result can't be trusted (photo, busy or patterned background), keep it whole
      if(!extract(src)){ src.photo=true; makePhoto(src); photosAdded++; }
      renderSources(); renderLib();
      // trace this image's sticker outlines now, one image at a time, so placing them later is instant
      for(const st of S.stickers.values()) if(st.srcId===src.id){ try{ vecOf(st); }catch(e){} await tick(); }
    }catch(e){ console.error(e); toast(`Couldn't read ${f.name}`); }
  }
  renderSources(); renderLib(); refresh();
  const fm=framesRemoved; framesRemoved=0;
  loading--;
  const n=S.stickers.size, fresh=[...S.stickers.keys()].filter(id=>!before.has(id));
  if(demo){ if(S.demo&&fresh.length) await placeStickers(fresh,'demo'); return fresh.length; }
  track('ss_file_upload',{ source, files:imgs.length, sheets:S.sources.length, stickers_found:n, photos:photosAdded, frame_removed:fm, file_type:imgs[0].type, file_kb:Math.round(imgs.reduce((a,f)=>a+f.size,0)/1024) });
  if(fresh.length){ await placeStickers(fresh,'auto'); closeAppPanel();
    if(photosAdded) toast(`${photosAdded===imgs.length?'':`Placed ${fresh.length} sticker${fresh.length>1?'s':''} · `}Kept ${photosAdded} image${photosAdded>1?'s':''} whole (no clean background to cut out). Tap one to pick a shape, or switch it to Cut out.`,6000); }
  else toast('No stickers found — try raising the background tolerance');
  if(!startedSent && n){ startedSent=true; klEvent('Started Sticker Sheet Maker',{ source, sheets:S.sources.length, stickers_found:n }); }
}

/* ---------------- baked-in checkerboard ----------------
   Images copied from an editor's screen often have the "transparent" checkerboard painted in.
   Detect it on the border (two flat colours, square cells in a regular grid), then flood in from the
   edge through pixels that match the checkerboard colour expected at that spot, so a white sticker
   border only goes where the pattern says white. Returns true (and clears those pixels' alpha). */
function knockOutChecker(im){
  const {data:px, width:w, height:h}=im, N=w*h;
  if(w<48||h<48) return false;
  const col=i=>[px[i*4],px[i*4+1],px[i*4+2]], d2=(a,b)=>(a[0]-b[0])**2+(a[1]-b[1])**2+(a[2]-b[2])**2;
  // 1. two colours near the edge: 2-means over a row and a column at each side. Each is the line (of the
  //    first 16 in from the edge) with the most contrast, so it runs through cells, not along a cell edge
  //    (a scaled screenshot blurs every cell edge into a grey line).
  //    Checkerboards are grey/white, so only neutral pixels count (stickers can touch the edge).
  const k=3, lum=c=>c[0]*0.3+c[1]*0.59+c[2]*0.11, neutral=c=>Math.max(c[0],c[1],c[2])-Math.min(c[0],c[1],c[2])<14;
  const best=(lines)=>{ let b=null, bv=-1; for(const L of lines){ let m=0, q=0, c=0; for(const i of L){ const p=col(i); if(!neutral(p)) continue; const v=lum(p); m+=v; q+=v*v; c++; }
    if(c<L.length*0.5) continue; const va=q/c-(m/c)**2; if(va>bv){ bv=va; b=L; } } return b; };
  const span=(n)=>[...Array(Math.min(16,n>>2)).keys()];
  const rowA=best(span(h).map(d=>[...Array(w).keys()].map(x=>(k+d)*w+x))), rowB=best(span(h).map(d=>[...Array(w).keys()].map(x=>(h-1-k-d)*w+x))),
        colA=best(span(w).map(d=>[...Array(h).keys()].map(y=>y*w+k+d))), colB=best(span(w).map(d=>[...Array(h).keys()].map(y=>y*w+w-1-k-d))),
        ring=rowA&&rowB&&colA&&colB?[...rowA,...rowB,...colA,...colB]:null;
  if(!ring) return false;
  const all=ring.map(col), cs=all.filter(neutral); if(cs.length<all.length*0.6) return false;
  let C1=cs.reduce((a,c)=>lum(c)>lum(a)?c:a), C2=cs.reduce((a,c)=>lum(c)<lum(a)?c:a);
  for(let it=0;it<6;it++){ const s1=[0,0,0,0], s2=[0,0,0,0];
    for(const c of cs){ const t=d2(c,C1)<=d2(c,C2)?s1:s2; t[0]+=c[0]; t[1]+=c[1]; t[2]+=c[2]; t[3]++; }
    if(!s1[3]||!s2[3]) return false; C1=[s1[0]/s1[3],s1[1]/s1[3],s1[2]/s1[3]]; C2=[s2[0]/s2[3],s2[1]/s2[3],s2[2]/s2[3]]; }
  const gap=Math.sqrt(d2(C1,C2)); if(gap<10||gap>140) return false;
  const T2=Math.max(10,Math.min(30,gap*0.45))**2;
  const cls=c=>{ const a=d2(c,C1), b=d2(c,C2); return a<=T2&&a<=b?1:b<=T2?2:0; };
  const seg=(c)=>{ const v=[C2[0]-C1[0],C2[1]-C1[1],C2[2]-C1[2]], L=v[0]**2+v[1]**2+v[2]**2, t=clamp(((c[0]-C1[0])*v[0]+(c[1]-C1[1])*v[1]+(c[2]-C1[2])*v[2])/L,0,1);
    return d2(c,[C1[0]+v[0]*t,C1[1]+v[1]*t,C1[2]+v[2]*t]); };
  let n1=0,n2=0,blend=0; for(const c of cs){ const q=cls(c); if(q===1)n1++; else if(q===2)n2++; else if(seg(c)<=T2) blend++; }
  const n=cs.length; if(n1/n<0.2||n2/n<0.2||(n1+n2+blend)/n<0.93) return false;
  // 2. cell edges. A scaled screenshot makes cells uneven (7,7,6,7…), so instead of one global grid, keep the
  //    actual colour changes found along the edge lines (top+bottom give the x edges, left+right the y edges)
  //    and fill gaps where art covers the line with evenly spaced edges.
  const trans=(idx)=>{ const tr=[]; let last=0, lastPos=-1;
    idx.forEach((i,pos)=>{ const q=cls(col(i)); if(!q) return; if(last&&q!==last&&pos-lastPos<=3) tr.push((lastPos+pos)/2); last=q; lastPos=pos; }); return tr; };
  const grid=(A,B,n)=>{ const ta=trans(A), tb=trans(B); if(ta.length<6&&tb.length<6) return null;
    const ds=[]; for(const t of [ta,tb]) for(let j=1;j<t.length;j++) ds.push(t[j]-t[j-1]); ds.sort((a,b)=>a-b);
    const sm=ds[ds.length>>1]; if(sm<3||sm>64||ds.filter(d=>Math.abs(d-sm)<=1.5).length/ds.length<0.6) return null;
    const raw=[...ta,...tb].sort((a,b)=>a-b), es=[];
    for(const t of raw){ if(es.length&&t-es[es.length-1].v<sm*0.5){ const e=es[es.length-1]; e.v=(e.v*e.c+t)/(e.c+1); e.c++; } else es.push({v:t,c:1}); }
    const out=[]; for(let j=0;j<es.length;j++){ const v=es[j].v;
      if(j){ const g=v-out[out.length-1], m=Math.round(g/sm); for(let q=1;q<m;q++) out.push(out[out.length-1]+g/m); }
      out.push(v); }
    while(out[0]>0) out.unshift(out[0]-sm); while(out[out.length-1]<n) out.push(out[out.length-1]+sm);
    const cell=new Int32Array(n), near=new Float32Array(n); let j=0;
    for(let v=0;v<n;v++){ while(j<out.length-1&&out[j+1]<=v) j++; cell[v]=j; near[v]=Math.min(v-out[j], j+1<out.length?out[j+1]-v:1e9); }
    return {sm, cell, near}; };
  const gx=grid(rowA,rowB,w), gy=grid(colA,colB,h); if(!gx||!gy||Math.abs(gx.sm-gy.sm)>1) return false;
  const edge=(v,g)=>g.near[v]<1.6, par=(x,y)=>(gx.cell[x]+gy.cell[y])&1;
  // which colour sits on even cells: whichever fits the ring better
  let fitA=0, fitB=0; for(const i of ring){ const x=i%w, y=(i/w)|0; if(edge(x,gx)||edge(y,gy)) continue; const q=cls(col(i)), p=par(x,y); if(q===(p?2:1)) fitA++; else if(q===(p?1:2)) fitB++; }
  if(Math.max(fitA,fitB)/(fitA+fitB||1)<0.85) return false;
  const even=fitA>=fitB?C1:C2, odd=fitA>=fitB?C2:C1;
  // 3. flood from the edge through pixels that match the expected colour (blends allowed on cell edges)
  const ok=i=>{ const x=i%w, y=(i/w)|0, c=col(i);
    if(edge(x,gx)||edge(y,gy)) return seg(c)<=T2;     // cell edges: either colour or a blend of the two
    return d2(c,par(x,y)?odd:even)<=T2; };
  const bg=new Uint8Array(N), st=new Int32Array(N); let sp=0;
  const push=i=>{ if(!bg[i]&&ok(i)){ bg[i]=1; st[sp++]=i; } };
  for(const i of ring) push(i);   // seed a few px in: the outermost px can be blurred
  while(sp){ const i=st[--sp], x=i%w, y=(i/w)|0; if(x>0)push(i-1); if(x<w-1)push(i+1); if(y>0)push(i-w); if(y<h-1)push(i+w); }
  for(let y=0;y<h;y++) for(let x=0;x<w;x++) if(x<k||y<k||x>=w-k||y>=h-k){ const xi=clamp(x,k,w-1-k), yi=clamp(y,k,h-1-k); bg[y*w+x]=bg[yi*w+xi]; }
  let cnt=0; for(let i=0;i<N;i++) cnt+=bg[i]; if(cnt<N*0.1) return false;
  // close 2px cracks the cell-edge tolerance can cut into white sticker borders
  const fg=new Uint8Array(N); for(let i=0;i<N;i++) fg[i]=bg[i]?0:1;
  const dOut=distTo(fg,w,h), nd=new Uint8Array(N); for(let i=0;i<N;i++) nd[i]=dOut[i]<=4?0:1;
  const dIn=distTo(nd,w,h);
  for(let i=0;i<N;i++) if(dIn[i]<=4) px[i*4+3]=0;
  return true;
}

/* ---------------- background removal ----------------
   1. Model the background as a smooth surface (handles lighting gradients, paper tint, phone photos):
      on a 1/8 grid, average the pixels that look like background, re-fit 3 times with less blur.
   2. Each pixel's distance from that local background, on a lightly blurred copy (ignores noise).
      Tolerance and edge strength adapt to the noise measured on the image border.
   3. Flood the background in from the border; it can't pass visible edges (outlines, cut lines), so
      white inside a sticker stays. Gaps under ~0.6% of the image are bridged, then holes filled.
   Also scores itself: a busy border, almost no background, or art across the edges → not trusted. */
function cutMask(src, boost=1){
  const {data:{data:px}, w, h}=src, N=w*h, sens=S.det.sens;
  // 1. background surface
  const f=8, gw=Math.ceil(w/f), gh=Math.ceil(h/f), G=gw*gh, gr=new Float32Array(G*3);
  for(let gy=0;gy<gh;gy++) for(let gx=0;gx<gw;gx++){ let r=0,g=0,b=0,n=0;
    for(let y=gy*f;y<Math.min(h,gy*f+f);y+=2) for(let x=gx*f;x<Math.min(w,gx*f+f);x+=2){ const o=(y*w+x)*4; r+=px[o]; g+=px[o+1]; b+=px[o+2]; n++; }
    const k=(gy*gw+gx)*3; gr[k]=r/n; gr[k+1]=g/n; gr[k+2]=b/n; }
  const med=a=>{ const t=Float32Array.from(a).sort(); return t[t.length>>1]; };
  const ring=[]; for(let gx=0;gx<gw;gx++){ ring.push(gx, (gh-1)*gw+gx); } for(let gy=1;gy<gh-1;gy++){ ring.push(gy*gw, gy*gw+gw-1); }
  const m0=[0,1,2].map(c=>med(ring.map(i=>gr[i*3+c])));
  const bgc=new Float32Array(G*3); for(let i=0;i<G;i++){ bgc[i*3]=m0[0]; bgc[i*3+1]=m0[1]; bgc[i*3+2]=m0[2]; }
  const dist=(i)=>{ const a=gr[i*3]-bgc[i*3], b=gr[i*3+1]-bgc[i*3+1], c=gr[i*3+2]-bgc[i*3+2]; return Math.sqrt(a*a+b*b+c*c); };
  let isB=new Uint8Array(G); for(let i=0;i<G;i++) isB[i]=dist(i)<45?1:0;
  const blur=(a,s)=>{ const r=Math.max(1,Math.round(s*0.9)), t=new Float32Array(G);   // 3 box passes ≈ gaussian
    for(let pass=0;pass<3;pass++){
      for(let y=0;y<gh;y++){ let acc=0,cnt=0; const row=y*gw;
        for(let x=-r;x<gw;x++){ if(x+r<gw){ acc+=a[row+x+r]; cnt++; } if(x-r-1>=0){ acc-=a[row+x-r-1]; cnt--; } if(x>=0) t[row+x]=acc/(2*r+1); } }
      for(let x=0;x<gw;x++){ let acc=0;
        for(let y=-r;y<gh;y++){ if(y+r<gh) acc+=t[(y+r)*gw+x]; if(y-r-1>=0) acc-=t[(y-r-1)*gw+x]; if(y>=0) a[y*gw+x]=acc/(2*r+1); } }
    } return a; };
  for(const [sg,tt] of [[12,30],[6,24],[3,24]]){
    const den=blur(Float32Array.from(isB),sg), ch=[0,1,2].map(c=>{ const a=new Float32Array(G); for(let i=0;i<G;i++) a[i]=gr[i*3+c]*isB[i]; return blur(a,sg); });
    for(let i=0;i<G;i++){ const wg=Math.min(1,den[i]/0.08); if(den[i]>1e-4) for(let c=0;c<3;c++) bgc[i*3+c]=ch[c][i]/den[i]*wg+bgc[i*3+c]*(1-wg); }
    for(let i=0;i<G;i++) isB[i]=dist(i)<tt?1:0;
  }
  // 2. distance from the local background (bilinear surface, 3×3 blurred pixels)
  const D=new Float32Array(N), tmp=new Float32Array(N*3), sm=new Float32Array(N*3);
  for(let y=0;y<h;y++) for(let x=0;x<w;x++){ const i=y*w+x, l=x>0?i-1:i, r=x<w-1?i+1:i;
    for(let c=0;c<3;c++) tmp[i*3+c]=(px[l*4+c]+2*px[i*4+c]+px[r*4+c])*0.25; }
  for(let y=0;y<h;y++) for(let x=0;x<w;x++){ const i=y*w+x, u=y>0?i-w:i, d=y<h-1?i+w:i;
    for(let c=0;c<3;c++) sm[i*3+c]=(tmp[u*3+c]+2*tmp[i*3+c]+tmp[d*3+c])*0.25; }
  for(let y=0;y<h;y++){ const fy=Math.min(gh-1,Math.max(0,(y+0.5)/f-0.5)), y0=Math.floor(fy), y1=Math.min(gh-1,y0+1), ty=fy-y0;
    for(let x=0;x<w;x++){ const fx=Math.min(gw-1,Math.max(0,(x+0.5)/f-0.5)), x0=Math.floor(fx), x1=Math.min(gw-1,x0+1), tx=fx-x0;
      let d2=0;
      for(let c=0;c<3;c++){
        const b=(bgc[(y0*gw+x0)*3+c]*(1-tx)+bgc[(y0*gw+x1)*3+c]*tx)*(1-ty)+(bgc[(y1*gw+x0)*3+c]*(1-tx)+bgc[(y1*gw+x1)*3+c]*tx)*ty;
        const e=sm[(y*w+x)*3+c]-b; d2+=e*e;
      }
      D[y*w+x]=Math.sqrt(d2);
    } }
  const R=Math.max(2,Math.min(8,Math.round(Math.min(w,h)*0.01))), ringIdx=[];
  for(let y=0;y<h;y+=2) for(let x=0;x<w;x+=2) if(x<R||y<R||x>=w-R||y>=h-R) ringIdx.push(y*w+x);
  const sig=Math.max(1, med(ringIdx.map(i=>D[i]))*1.4826), tol=Math.max(10,4.5*sig)*sens*boost;   // boost: looser background match (see extract)
  // edges: max Sobel over R,G,B, threshold relative to the border's own texture
  const g=new Float32Array(N);
  for(let y=1;y<h-1;y++) for(let x=1;x<w-1;x++){ const i=y*w+x; let m=0;
    for(let c=0;c<3;c++){ const p=k=>px[k*4+c];
      const gx=(p(i-w+1)+2*p(i+1)+p(i+w+1))-(p(i-w-1)+2*p(i-1)+p(i+w-1)), gy=(p(i+w-1)+2*p(i+w)+p(i+w+1))-(p(i-w-1)+2*p(i-w)+p(i-w+1));
      const v=Math.abs(gx)+Math.abs(gy); if(v>m) m=v; }
    g[i]=m; }
  const gsig=med(ringIdx.filter(i=>{ const x=i%w, y=(i/w)|0; return x>0&&y>0&&x<w-1&&y<h-1; }).map(i=>g[i]))*1.4826+1;
  const gThr=Math.max(20,6*gsig)/sens;
  const wall0=new Uint8Array(N); for(let i=0;i<N;i++) if(g[i]>gThr&&D[i]>tol*0.35) wall0[i]=1;
  const wall=new Uint8Array(N);
  for(let y=0;y<h;y++) for(let x=0;x<w;x++){ const i=y*w+x; if(wall0[i]||(x>0&&wall0[i-1])||(x<w-1&&wall0[i+1])||(y>0&&wall0[i-w])||(y<h-1&&wall0[i+w])) wall[i]=1; }
  // 3. flood the background in from the border
  const bg=new Uint8Array(N), st=new Int32Array(N); let sp=0;
  const push=i=>{ if(!bg[i]&&!wall[i]&&D[i]<tol){ bg[i]=1; st[sp++]=i; } };
  for(let x=0;x<w;x++){ push(x); push((h-1)*w+x); } for(let y=0;y<h;y++){ push(y*w); push(y*w+w-1); }
  while(sp){ const i=st[--sp], x=i%w, y=(i/w)|0; if(x>0)push(i-1); if(x<w-1)push(i+1); if(y>0)push(i-w); if(y<h-1)push(i+w); }
  // the edge band stops the flood 1-2px short; take back background-coloured pixels beside it
  for(let pass=0;pass<3;pass++){ const add=[];
    for(let y=1;y<h-1;y++) for(let x=1;x<w-1;x++){ const i=y*w+x; if(bg[i]||D[i]>=tol*0.7) continue; if(bg[i-1]||bg[i+1]||bg[i-w]||bg[i+w]) add.push(i); }
    for(const i of add) bg[i]=1; }
  let fg=new Uint8Array(N); for(let i=0;i<N;i++) fg[i]=bg[i]?0:1;
  // drop noise and hairlines: opening, 2 × 3×3
  const morph=(a,erode)=>{ const o=new Uint8Array(N);
    for(let y=0;y<h;y++) for(let x=0;x<w;x++){ const i=y*w+x; let v=a[i];
      if(erode){ if(v&&((x>0&&!a[i-1])||(x<w-1&&!a[i+1])||(y>0&&!a[i-w])||(y<h-1&&!a[i+w]))) v=0; }
      else if(!v&&((x>0&&a[i-1])||(x<w-1&&a[i+1])||(y>0&&a[i-w])||(y<h-1&&a[i+w]))) v=1;
      o[i]=v; } return o; };
  fg=morph(morph(morph(morph(fg,1),1),0),0);
  // Each piece on its own: bridge small gaps in its outline, then fill what it encloses (white sticker
  // borders the same colour as the background). Done per piece, so background between stickers that sit
  // close together is never bridged or filled, and neighbouring stickers stay separate.
  { const r=Math.max(1,Math.round(Math.hypot(w,h)*0.006)), r2=r*r, L=new Int32Array(N), stk=new Int32Array(N), out=new Uint8Array(N); let nl=0;
    for(let s0=0;s0<N;s0++){ if(!fg[s0]||L[s0]) continue;
      nl++; let sp=0, x0=w, y0=h, x1=-1, y1=-1; stk[sp++]=s0; L[s0]=nl;
      while(sp){ const i=stk[--sp], x=i%w, y=(i/w)|0; if(x<x0)x0=x; if(x>x1)x1=x; if(y<y0)y0=y; if(y>y1)y1=y;
        if(x>0&&fg[i-1]&&!L[i-1]){ L[i-1]=nl; stk[sp++]=i-1; } if(x<w-1&&fg[i+1]&&!L[i+1]){ L[i+1]=nl; stk[sp++]=i+1; }
        if(y>0&&fg[i-w]&&!L[i-w]){ L[i-w]=nl; stk[sp++]=i-w; } if(y<h-1&&fg[i+w]&&!L[i+w]){ L[i+w]=nl; stk[sp++]=i+w; } }
      // tiny bits: no bridging needed, keep as they are
      if(x1-x0<8||y1-y0<8){ for(let y=y0;y<=y1;y++) for(let x=x0;x<=x1;x++){ const i=y*w+x; if(L[i]===nl) out[i]=1; } continue; }
      const pad=r+2, cx0=Math.max(0,x0-pad), cy0=Math.max(0,y0-pad), cw=Math.min(w-1,x1+pad)-cx0+1, ch=Math.min(h-1,y1+pad)-cy0+1, CN=cw*ch, m=new Uint8Array(CN);
      for(let y=0;y<ch;y++) for(let x=0;x<cw;x++) if(L[(y+cy0)*w+x+cx0]===nl) m[y*cw+x]=1;
      const dOut=distTo(m,cw,ch), nd=new Uint8Array(CN); for(let i=0;i<CN;i++) nd[i]=dOut[i]<=r2?0:1;   // nd = NOT dilated
      const dIn=distTo(nd,cw,ch), cl=new Uint8Array(CN); for(let i=0;i<CN;i++) cl[i]=dIn[i]>r2||m[i]?1:0;
      const clF=fillHoles(cl.slice(),cw,ch), mF=fillHoles(m.slice(),cw,ch);
      for(let y=0;y<ch;y++) for(let x=0;x<cw;x++){ const k=y*cw+x; if(mF[k]||(clF[k]&&!cl[k])) out[(y+cy0)*w+x+cx0]=1; }
    }
    fg=out; }
  // confidence
  let plain=0; for(const i of ringIdx) if(D[i]<tol) plain++; plain/=ringIdx.length;
  let area=0, edge=0; for(let i=0;i<N;i++) if(fg[i]) area++;
  for(const i of ringIdx) if(fg[i]) edge++;
  const frac=area/N, edgeFrac=edge/ringIdx.length;
  const ok = plain>=0.7 && frac>=0.004 && frac<=0.85 && edgeFrac<0.2;
  return {mask:fg, ok, plain, frac, edgeFrac, tol, sig};
}
// how many sticker-sized pieces a mask has, and the biggest one's share of the total
function pieceStats(m,w,h){
  const N=w*h, seen=new Uint8Array(N), st=new Int32Array(N), min=N*0.0015; let n=0, total=0, max=0;
  for(let s0=0;s0<N;s0++){ if(!m[s0]||seen[s0]) continue; let sp=0, a=0; st[sp++]=s0; seen[s0]=1;
    while(sp){ const i=st[--sp], x=i%w; a++;
      if(x>0&&m[i-1]&&!seen[i-1]){ seen[i-1]=1; st[sp++]=i-1; } if(x<w-1&&m[i+1]&&!seen[i+1]){ seen[i+1]=1; st[sp++]=i+1; }
      if(i>=w&&m[i-w]&&!seen[i-w]){ seen[i-w]=1; st[sp++]=i-w; } if(i<N-w&&m[i+w]&&!seen[i+w]){ seen[i+w]=1; st[sp++]=i+w; } }
    total+=a; if(a>=min){ n++; if(a>max) max=a; } }
  return {n, maxFrac:total?max/total:0};
}
function extract(src){
  const {data:{data:px}, w, h} = src, N=w*h, d=S.det;
  const mask=new Uint8Array(N);
  // transparent input?
  let clear=0; for(let i=3;i<px.length;i+=16) if(px[i]<24) clear++;
  const isTransparent = clear > N/16*0.02;
  if(isTransparent){
    // already cut out: keep faint watercolor edges and thin stems
    for(let i=0;i<N;i++) mask[i]=px[i*4+3]>=40?1:0;
  } else {
    let r=cutMask(src);
    // Packed sheets on a coloured background (JPEG colour bleed round each sticker) can close the
    // thin gaps between stickers, so they come out as one piece. If the result looks merged, try a
    // looser background match and keep it only if it clearly separates more stickers.
    if(r.ok&&S.det.sens===1){ let p=pieceStats(r.mask,w,h);
      for(const b of [2,4]){ const r2=cutMask(src,b); if(!r2.ok) continue; const p2=pieceStats(r2.mask,w,h);
        if(p2.n>=p.n+Math.max(2,Math.ceil(p.n*0.05))&&r2.frac>=r.frac*0.75){ r=r2; p=p2; } } }
    mask.set(r.mask); src.conf=r;
  }
  // labelling mask (optionally grown so detached bits stay with their sticker)
  // pieces are labelled as they are (1px grace only): growing them first glued neighbouring stickers on packed
  // sheets together. Detached bits (sparkles, splashes) still join their sticker through the small-piece rule below.
  const merge=1;
  let lab=mask;
  if(merge>0){ const dt=distTo(mask,w,h), r2=merge*merge; lab=new Uint8Array(N); for(let i=0;i<N;i++) lab[i]=dt[i]<=r2?1:0; }
  const L=new Int32Array(N), st=new Int32Array(N), comps=[];
  let nl=0;
  for(let s=0;s<N;s++){
    if(!lab[s]||L[s]) continue;
    nl++; let sp=0; st[sp++]=s; L[s]=nl;
    let x0=w,y0=h,x1=-1,y1=-1,area=0;
    while(sp){ const i=st[--sp], x=i%w, y=(i/w)|0;
      if(mask[i]){ area++; if(x<x0)x0=x; if(x>x1)x1=x; if(y<y0)y0=y; if(y>y1)y1=y; }
      for(let dy=-1;dy<=1;dy++){ const yy=y+dy; if(yy<0||yy>=h) continue;
        for(let dx=-1;dx<=1;dx++){ const xx=x+dx; if(xx<0||xx>=w) continue; const j=yy*w+xx; if(lab[j]&&!L[j]){ L[j]=nl; st[sp++]=j; } } }
    }
    if(x1<0) continue;
    comps.push({lbl:nl,x0,y0,x1,y1,area});
  }
  // Small pieces (under 6% of the biggest piece) that sit close to a big one become part of it.
  const lblMap=new Int32Array(nl+1); for(let i=0;i<=nl;i++) lblMap[i]=i;
  { const maxA=Math.max(1,...comps.map(c=>c.area)), near=Math.max(merge, Math.round(Math.max(w,h)*0.03));
    // (transparent images used to fold anything under half the biggest piece into a neighbour, which
    //  grouped separate small stickers on packed sheets; now only specks and pieces inside another piece's box do)
    const cut=0.06, big=comps.filter(c=>c.area>=maxA*cut), small=comps.filter(c=>c.area<maxA*cut);
    for(const c of small){
      let best=null, bd=Infinity;
      for(const g of big){ const dx=Math.max(0,g.x0-c.x1,c.x0-g.x1), dy=Math.max(0,g.y0-c.y1,c.y0-g.y1), dd=Math.hypot(dx,dy); if(dd<bd){ bd=dd; best=g; } }
      // transparent clip art: a piece inside the main piece's outline box belongs to it
      if(best&&bd<=near){ lblMap[c.lbl]=best.lbl; best.x0=Math.min(best.x0,c.x0); best.y0=Math.min(best.y0,c.y0); best.x1=Math.max(best.x1,c.x1); best.y1=Math.max(best.y1,c.y1); c.merged=true; }
    }
    for(let i=comps.length-1;i>=0;i--){ const c=comps[i]; if(c.merged){ comps.splice(i,1); continue; }
      c.bw=c.x1-c.x0+1; c.bh=c.y1-c.y0+1;
      // specks: tiny next to the image, or tiny next to the biggest piece
      if(Math.max(c.bw,c.bh) < 24 || c.area < (isTransparent?150:Math.max(N*0.0004, maxA*0.03))) comps.splice(i,1); }
    // transparent clip art: a piece sitting wholly inside another piece's outline box belongs to it (eyes, sparkles)
    if(isTransparent) for(let i=comps.length-1;i>=0;i--){ const c=comps[i];
      const g=comps.find(o=>o!==c&&o.area>c.area&&c.x0>=o.x0&&c.x1<=o.x1&&c.y0>=o.y0&&c.y1<=o.y1);
      if(g){ lblMap[c.lbl]=g.lbl; comps.splice(i,1); } }
  }
  // drop components that are the whole image (nothing detected)
  const kept=comps;
  kept.sort((a,b)=>{ const ra=Math.round(a.y0/(h/8)), rb=Math.round(b.y0/(h/8)); return ra-rb || a.x0-b.x0; });

  const old=[...S.stickers.values()].filter(s=>s.srcId===src.id);
  const used=new Set(), fresh=[];
  const longest=Math.max(1,...kept.map(c=>Math.max(c.bw,c.bh)));
  // biggest sticker = S.det.big inches, but a packed sheet of many stickers is shrunk so the whole
  // image would fit on one page (with room for offsets), instead of placing a few and leaving the rest out.
  // Once they move the "Largest sticker" slider, their size is used as is.
  const fitPage=kept.length>1&&!S.det.bigSet?Math.max(w/(S.sheetW-2*S.margin), h/(S.sheetH-2*S.margin))/0.85:0;
  const ppi=Math.max(longest/S.det.big, fitPage);
  kept.forEach((c,idx)=>{
    const P=2, cw=c.bw+P*2, ch=c.bh+P*2;
    const m=new Uint8Array(cw*ch);
    for(let y=0;y<c.bh;y++) for(let x=0;x<c.bw;x++){ const i=(c.y0+y)*w+c.x0+x; if(mask[i]&&lblMap[L[i]]===c.lbl) m[(y+P)*cw+x+P]=1; }
    fillHoles(m,cw,ch);
    const dep=!isTransparent?depthIn(m,cw,ch):null; // 1px halo trim only when we removed a background
    const cv=mk(cw,ch), cx=cv.getContext('2d',{willReadFrequently:true}), id=cx.createImageData(cw,ch), o=id.data;
    for(let y=0;y<ch;y++) for(let x=0;x<cw;x++){
      const k=y*cw+x; if(!m[k]) continue;
      let a=1; if(dep){ a=clamp(Math.sqrt(dep[k])-1,0,1); if(a<=0) continue; }
      const sx=c.x0+x-P, sy=c.y0+y-P; if(sx<0||sy<0||sx>=w||sy>=h) continue;
      const si=(sy*w+sx)*4, q=k*4; o[q]=px[si]; o[q+1]=px[si+1]; o[q+2]=px[si+2]; o[q+3]=Math.round(a*(px[si+3]));
    }
    cx.putImageData(id,0,0);
    const bbox=[c.x0,c.y0,c.bw,c.bh];
    // keep the same id if this matches a sticker we had before (so placed copies survive re-detection)
    let match=null, best=0.5;
    for(const s of old){ if(used.has(s.id)) continue; const v=iou(s.bbox,bbox); if(v>best){best=v;match=s;} }
    const st2={id:match?match.id:nid(), srcId:src.id, name:`${src.name}-${idx+1}`, canvas:cv, w:cw, h:ch, bbox, natW:cw/ppi, thumb:null};
    if(match) used.add(match.id);
    fresh.push(st2);
  });
  for(const s of old) S.stickers.delete(s.id);
  for(const s of fresh) S.stickers.set(s.id,s);
  src.count=fresh.length;
  // Can we trust this cut-out? If not, the caller keeps the image whole instead.
  return isTransparent || !!(src.conf && src.conf.ok && fresh.length>=1 && fresh.length<=60);
}
/* ---------------- regular photos ----------------
   'transparent' = already cut out; 'sheet' = opaque with a plain border (stickers on a plain
   background); 'photo' = opaque and the border isn't one colour (a snapshot, a scene). */
function imageKind(im){
  const {data:px, width:w, height:h}=im, N=w*h;
  let clear=0; for(let i=3;i<px.length;i+=16) if(px[i]<24) clear++;
  if(clear > N/16*0.02) return 'transparent';
  const rs=[],gs=[],bs=[], idx=[];
  const samp=i=>{ idx.push(i); rs.push(px[i*4]); gs.push(px[i*4+1]); bs.push(px[i*4+2]); };
  const step=Math.max(1,Math.round(Math.max(w,h)/400));
  for(let x=0;x<w;x+=step){ samp(x); samp((h-1)*w+x); samp(Math.min(h-1,3)*w+x); samp(Math.max(0,h-4)*w+x); }
  for(let y=0;y<h;y+=step){ samp(y*w); samp(y*w+w-1); samp(y*w+Math.min(w-1,3)); samp(y*w+Math.max(0,w-4)); }
  const med=a=>a.slice().sort((p,q)=>p-q)[a.length>>1];
  const br=med(rs), bg=med(gs), bb=med(bs), t2=36*36*3;
  let near=0; for(const i of idx){ const o=i*4, r=px[o]-br, g=px[o+1]-bg, b=px[o+2]-bb; if(r*r+g*g+b*b<=t2) near++; }
  return near/idx.length < 0.6 ? 'busy' : 'plain';
}
const SHAPES=[
  ['rounded','Rounded'],['circle','Circle'],['oval','Oval'],['square','Square'],['heart','Heart'],['star','Star'],['full','Full photo']];
const SHAPE_ICON={
  rounded:'<rect x="3" y="5" width="18" height="14" rx="4"/>', circle:'<circle cx="12" cy="12" r="8.5"/>',
  oval:'<ellipse cx="12" cy="12" rx="6.5" ry="9"/>', square:'<rect x="4" y="4" width="16" height="16"/>',
  heart:'<path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.4 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z"/>',
  star:'<path d="M12 3.2l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7z"/>',
  full:'<rect x="3" y="5" width="18" height="14"/><path d="M3 16l5-5 4 4 3-3 6 6" fill="none" stroke-width="1.6"/>'};
let photoShape='rounded';           // last shape picked: new photos start with it
/* shape outline in a w×h box */
function shapePath(shape,w,h){
  const p=new Path2D(), m=Math.min(w,h);
  if(shape==='circle'||shape==='oval'){ p.ellipse(w/2,h/2,w/2,h/2,0,0,Math.PI*2); }
  else if(shape==='rounded'){ p.roundRect(0,0,w,h,m*0.12); }
  else if(shape==='heart'){ const X=v=>v*w, Y=v=>v*h;
    p.moveTo(X(.5),Y(.97)); p.bezierCurveTo(X(.18),Y(.76),X(0),Y(.55),X(0),Y(.31)); p.bezierCurveTo(X(0),Y(.13),X(.13),Y(.02),X(.29),Y(.02));
    p.bezierCurveTo(X(.39),Y(.02),X(.46),Y(.08),X(.5),Y(.18)); p.bezierCurveTo(X(.54),Y(.08),X(.61),Y(.02),X(.71),Y(.02));
    p.bezierCurveTo(X(.87),Y(.02),X(1),Y(.13),X(1),Y(.31)); p.bezierCurveTo(X(1),Y(.55),X(.82),Y(.76),X(.5),Y(.97)); p.closePath(); }
  else if(shape==='star'){ for(let i=0;i<10;i++){ const a=-Math.PI/2+i*Math.PI/5, r=i%2?0.24:0.5; const x=w/2+Math.cos(a)*r*w*1.02, y=h*0.54+Math.sin(a)*r*h*1.04; i?p.lineTo(x,y):p.moveTo(x,y); } p.closePath(); }
  else p.rect(0,0,w,h);   // square, full
  return p;
}
/* crop the photo to the shape (centred); fixed-ratio shapes take the biggest centred box */
/* crop box for a shape: the biggest box of the shape's ratio, divided by zoom, centred on (cx,cy)
   (0–1 of the image) and kept inside the image. src.crop = {zoom, cx, cy}, set in the Adjust editor. */
function shapeRatio(src,shape){ const W=src.w, H=src.h; return shape==='oval'?(W>=H?4/3:3/4):(shape==='rounded'||shape==='full'?W/H:1); }
function cropBox(src,shape,crop){
  const W=src.w, H=src.h, ratio=shapeRatio(src,shape), c=crop||src.crop||{zoom:1,cx:0.5,cy:0.5};
  let cw=W, ch=H; if(W/H>ratio) cw=H*ratio; else ch=W/ratio;
  const z=clamp(c.zoom||1,1,8); cw/=z; ch/=z;
  const mx=clamp(c.cx*W,cw/2,W-cw/2), my=clamp(c.cy*H,ch/2,H-ch/2);
  return { x:mx-cw/2, y:my-ch/2, w:cw, h:ch, cx:mx/W, cy:my/H, zoom:z };
}
function photoCanvas(src,shape){
  const b=cropBox(src,shape), cw=Math.max(8,Math.round(b.w)), ch=Math.max(8,Math.round(b.h));
  const x0=Math.round(b.x), y0=Math.round(b.y), P=2;
  const cv=mk(cw+P*2,ch+P*2), cx=cv.getContext('2d',{willReadFrequently:true});
  cx.translate(P,P); cx.save(); cx.clip(shapePath(shape,cw,ch)); cx.imageSmoothingQuality='high'; cx.drawImage(src.canvas,x0,y0,cw,ch,0,0,cw,ch); cx.restore();
  return {cv, bbox:[x0,y0,cw,ch]};
}
/* one sticker per photo; reshaping keeps its id so placed copies update in place */
function makePhoto(src, shape){
  const old=[...S.stickers.values()].find(s=>s.srcId===src.id);
  shape=shape||(old&&old.photo)||photoShape;
  const {cv,bbox}=photoCanvas(src,shape);
  const longIn=S.det.big, natW=cv.width>=cv.height?longIn:longIn*cv.width/cv.height;
  const st={id:old?old.id:nid(), srcId:src.id, name:src.name, canvas:cv, w:cv.width, h:cv.height, bbox, natW, thumb:null, photo:shape};
  // swap it in where the old one sat, so the step 1 grid keeps its order
  const m=new Map(); let put=false;
  for(const [id,x] of S.stickers){ if(x.srcId===src.id){ if(!put){ m.set(st.id,st); put=true; } } else m.set(id,x); }
  if(!put) m.set(st.id,st); S.stickers=m; src.count=1;
  for(const k of [...cache.keys()]) if(k.startsWith(st.id+'|')) cache.delete(k);
  return st;
}
function setShape(stId, shape){
  const st=S.stickers.get(stId); if(!st||!st.photo) return;
  const src=S.sources.find(x=>x.id===st.srcId); if(!src) return;
  const oldLong=new Map(); S.sheets.forEach(sh=>sh.items.forEach(it=>{ if(it.stickerId===stId) oldLong.set(it,Math.max(it.widthIn,it.widthIn*st.h/st.w)); }));
  const nst=makePhoto(src,shape); photoShape=shape;
  // keep each placed copy's longest side the same size
  for(const [it,L] of oldLong) it.widthIn=nst.w>=nst.h?L:L*nst.w/nst.h;
  track('ss_photo_shape',{ shape });
  renderLib(); refresh(); try{ vecOf(nst); }catch(e){}
}
/* wrong guess: photo → cut-out stickers, or cut-out image → one photo */
async function switchKind(src, toPhoto){
  snapshot();
  const oldIds=new Set([...S.stickers.values()].filter(x=>x.srcId===src.id).map(x=>x.id));
  S.sheets.forEach(sh=>sh.items=sh.items.filter(i=>!oldIds.has(i.stickerId)));
  for(const id of oldIds){ grave.stickers.set(id,S.stickers.get(id)); S.stickers.delete(id); }
  for(const k of [...cache.keys()]) if(oldIds.has(+k.split('|')[0])) cache.delete(k);
  src.photo=toPhoto; S.sel=null;
  if(toPhoto) makePhoto(src); else extract(src);
  let ids=[...S.stickers.values()].filter(x=>x.srcId===src.id).map(x=>x.id);
  // nothing to cut out (a photo with no background): keep it whole rather than lose it
  if(!toPhoto&&!ids.length){ src.photo=true; makePhoto(src); ids=[...S.stickers.values()].filter(x=>x.srcId===src.id).map(x=>x.id);
    toast(`Couldn’t find a background to remove in “${src.name}”, so it stays whole.`,5000); }
  track('ss_photo_switch',{ to:toPhoto?'photo':'cutout', stickers:ids.length });
  renderSources(); renderLib(); refresh();
  if(ids.length) await placeStickers(ids,'switch');
}
function iou(a,b){ const x0=Math.max(a[0],b[0]),y0=Math.max(a[1],b[1]),x1=Math.min(a[0]+a[2],b[0]+b[2]),y1=Math.min(a[1]+a[3],b[1]+b[3]);
  const i=Math.max(0,x1-x0)*Math.max(0,y1-y0); return i/(a[2]*a[3]+b[2]*b[3]-i); }

let reT;
function redetect(){
  clearTimeout(reT);
  reT=setTimeout(async()=>{
    if(!S.sources.length) return;
    toast('Re-detecting stickers…',30000); await tick();
    for(const s of S.sources) if(!s.photo) extract(s);
    let lost=0; for(const sh of S.sheets){ const n=sh.items.length; sh.items=sh.items.filter(i=>S.stickers.has(i.stickerId)); lost+=n-sh.items.length; }
    cache.clear(); renderSources(); renderLib(); refresh();
    toast(`${S.stickers.size} stickers found`+(lost?` · ${lost} placed sticker${lost>1?'s':''} no longer matched and were removed`:''));
  },350);
}

/* ---------------- item rendering (offset + cut line) ---------------- */
const cache=new Map();
const effOff=it=>it.offset==null?S.offset:it.offset;
const effSm=it=>it.smooth==null?S.smooth:it.smooth;
/* fallback if the vector library fails to load: distance-field offset */
function renderItemRaster(it, ppi, key){
  const st=S.stickers.get(it.stickerId); if(!st) return null;
  const off=effOff(it), paper=S.mode==='paper';
  const line=(S.lineOn&&off>0)?Math.max(S.lineW, 1/ppi):0;
  const W=Math.max(1,Math.round(it.widthIn*ppi)), H=Math.max(1,Math.round(W*st.h/st.w));
  const r=off*ppi, lw=line*ppi, pad=Math.ceil(r+3);
  const cw=W+pad*2, ch=H+pad*2;
  const base=mk(cw,ch), bx=base.getContext('2d',{willReadFrequently:true});
  bx.imageSmoothingEnabled=true; bx.imageSmoothingQuality='high';
  bx.drawImage(st.canvas,pad,pad,W,H);
  let out=base;
  if(r>0.3){
    const src=bx.getImageData(0,0,cw,ch).data, N=cw*ch, m=new Uint8Array(N);
    for(let i=0;i<N;i++) m[i]=src[i*4+3]>100?1:0;
    const dt=distTo(m,cw,ch), bin=new Uint8Array(N);
    for(let i=0;i<N;i++) bin[i]=dt[i]<=r*r?1:0;
    const filled=fillHoles(bin.slice(),cw,ch);
    const din=lw>0?depthIn(filled,cw,ch):null;
    const [or,og,ob]=hexRGB(S.offColor), [lr,lg,lb]=hexRGB(S.lineC);
    out=mk(cw,ch); const ox=out.getContext('2d',{willReadFrequently:true}), id=ox.createImageData(cw,ch), o=id.data;
    for(let i=0;i<N;i++){
      let a=filled[i]&&!bin[i]?1:clamp(r-Math.sqrt(dt[i])+0.5,0,1);
      if(a<=0) continue;
      let t=0;
      if(din){ t = filled[i] ? clamp(lw+0.5-Math.sqrt(din[i]),0,1) : 1; }
      const q=i*4; o[q]=or+(lr-or)*t; o[q+1]=og+(lg-og)*t; o[q+2]=ob+(lb-ob)*t; o[q+3]=a*255;
    }
    ox.putImageData(id,0,0); ox.drawImage(base,0,0);
  }
  const res={canvas:out,cw,ch,alpha:null};
  if(ppi===PREV){ res.alpha=out.getContext('2d',{willReadFrequently:true}).getImageData(0,0,cw,ch).data; }
  cache.set(key,res);
  return res;
}

/* ---------- vector edges: sub-pixel contour → smooth curves → true round offset ---------- */
// Trace an iso-line of the (lightly blurred) sticker alpha with marching squares, so edge points sit
// between pixels instead of on the pixel staircase. Returns closed loops in sticker-canvas pixel coords.
const TRACE_MAX=900;   // outlines are traced at no more than this many px, then scaled up (they're smoothed anyway)
function traceScale(st){ return Math.min(1, TRACE_MAX/Math.max(st.w,st.h)); }
function traceLoops(st, iso){
  const k=traceScale(st), sw=Math.max(1,Math.round(st.w*k)), sh=Math.max(1,Math.round(st.h*k));
  const P=4, w=sw+P*2, h=sh+P*2, ux=st.w/sw, uy=st.h/sh;
  let F=new Float32Array(w*h);
  if(!st.alphaFull) st.alphaFull=st.canvas.getContext('2d',{willReadFrequently:true}).getImageData(0,0,st.w,st.h).data;
  const a=st.alphaFull;
  if(k>=1){ for(let y=0;y<sh;y++) for(let x=0;x<sw;x++) F[(y+P)*w+x+P]=a[(y*st.w+x)*4+3]/255; }
  else { // max-pool downscale: a thin stem keeps its full strength instead of being averaged away
    for(let y=0;y<st.h;y++){ const ty=Math.min(sh-1,(y*k)|0), row=(ty+P)*w+P;
      for(let x=0;x<st.w;x++){ const v=a[(y*st.w+x)*4+3]; if(!v) continue; const i=row+Math.min(sw-1,(x*k)|0), f=v/255; if(f>F[i]) F[i]=f; } } }
  // two box-blur passes (~Gaussian σ≈1.2px) to melt the pixel staircase
  const T=new Float32Array(w*h);
  for(let pass=0;pass<2;pass++){
    for(let y=0;y<h;y++) for(let x=1;x<w-1;x++){ const i=y*w+x; T[i]=(F[i-1]+F[i]+F[i+1])/3; }
    for(let y=1;y<h-1;y++) for(let x=0;x<w;x++){ const i=y*w+x; F[i]=(T[i-w]+T[i]+T[i+w])/3; }
  }
  const pts=new Map(), adj=new Map();
  const lerp=(v0,v1)=>(iso-v0)/(v1-v0);
  const hid=(x,y)=>(y*w+x)*2, vid=(x,y)=>(y*w+x)*2+1;
  const pt=(id,x,y)=>{ if(!pts.has(id)) pts.set(id,[(x-P+0.5)*ux,(y-P+0.5)*uy]); return id; };
  const link=(e1,e2)=>{ (adj.get(e1)||adj.set(e1,[]).get(e1)).push(e2); (adj.get(e2)||adj.set(e2,[]).get(e2)).push(e1); };
  for(let y=0;y<h-1;y++) for(let x=0;x<w-1;x++){
    const tl=F[y*w+x], tr=F[y*w+x+1], br=F[(y+1)*w+x+1], bl=F[(y+1)*w+x];
    const c=(tl>=iso?8:0)|(tr>=iso?4:0)|(br>=iso?2:0)|(bl>=iso?1:0);
    if(c===0||c===15) continue;
    const Te=()=>pt(hid(x,y),x+lerp(tl,tr),y), Be=()=>pt(hid(x,y+1),x+lerp(bl,br),y+1);
    const Le=()=>pt(vid(x,y),x,y+lerp(tl,bl)), Re=()=>pt(vid(x+1,y),x+1,y+lerp(tr,br));
    const mid=(tl+tr+br+bl)/4>=iso;
    switch(c){
      case 1: case 14: link(Le(),Be()); break;
      case 2: case 13: link(Be(),Re()); break;
      case 3: case 12: link(Le(),Re()); break;
      case 4: case 11: link(Te(),Re()); break;
      case 6: case 9:  link(Te(),Be()); break;
      case 7: case 8:  link(Le(),Te()); break;
      case 5: if(mid){ link(Le(),Te()); link(Be(),Re()); } else { link(Te(),Re()); link(Le(),Be()); } break;
      case 10: if(mid){ link(Te(),Re()); link(Le(),Be()); } else { link(Le(),Te()); link(Be(),Re()); } break;
    }
  }
  const seen=new Set(), loops=[];
  for(const startId of adj.keys()){
    if(seen.has(startId)) continue;
    const loop=[]; let prev=-1, cur=startId;
    while(cur!==undefined && !seen.has(cur)){
      seen.add(cur); loop.push(pts.get(cur));
      const nb=adj.get(cur); const nxt=nb[0]!==prev?nb[0]:nb[1]; prev=cur; cur=nxt;
    }
    if(loop.length>=6 && Math.abs(polyArea(loop))>6*ux*uy) loops.push(rdp(loop,0.3*ux));
  }
  return loops;
}
function polyArea(p){ let a=0; for(let i=0,j=p.length-1;i<p.length;j=i++) a+=(p[j][0]+p[i][0])*(p[j][1]-p[i][1]); return a/2; }
function rdp(loop,eps){
  // closed-loop Douglas–Peucker: split at the two farthest-apart points
  const n=loop.length; if(n<8) return loop;
  let far=0,best=-1; for(let i=1;i<n;i++){ const d=(loop[i][0]-loop[0][0])**2+(loop[i][1]-loop[0][1])**2; if(d>best){best=d;far=i;} }
  const simp=(pts)=>{
    const keep=new Uint8Array(pts.length); keep[0]=keep[pts.length-1]=1; const st=[[0,pts.length-1]];
    while(st.length){ const [a,b]=st.pop(); let md=-1,mi=-1; const [ax,ay]=pts[a],[bx,by]=pts[b], dx=bx-ax, dy=by-ay, L=Math.hypot(dx,dy)||1;
      for(let i=a+1;i<b;i++){ const d=Math.abs(dy*pts[i][0]-dx*pts[i][1]+bx*ay-by*ax)/L; if(d>md){md=d;mi=i;} }
      if(md>eps){ keep[mi]=1; st.push([a,mi],[mi,b]); } }
    return pts.filter((_,i)=>keep[i]);
  };
  const A=simp(loop.slice(0,far+1)), B=simp(loop.slice(far).concat([loop[0]]));
  return A.slice(0,-1).concat(B.slice(0,-1));
}
// quadratic B-spline through loop midpoints: C1-smooth, no corners, no staircase
function splinePath(loops,s,ox,oy){
  const p=new Path2D();
  for(const L of loops){
    const n=L.length; if(n<3) continue;
    const X=i=>L[(i+n)%n][0]*s+ox, Y=i=>L[(i+n)%n][1]*s+oy;
    p.moveTo((X(0)+X(1))/2,(Y(0)+Y(1))/2);
    for(let i=1;i<=n;i++) p.quadraticCurveTo(X(i),Y(i),(X(i)+X(i+1))/2,(Y(i)+Y(i+1))/2);
    p.closePath();
  }
  return p;
}
function vecOf(st){
  if(!st.vec) st.vec={art:traceLoops(st,0.6), base:traceLoops(st,0.5), off:new Map()};
  return st.vec;
}
// true vector offset (round joins) of the sticker outline, holes dropped so the offset is one solid shape
function offsetLoops(st,d,sm){
  const v=vecOf(st), k=d.toFixed(2)+'|'+sm.toFixed(2); if(v.off.has(k)) return v.off.get(k);
  const C=window.ClipperLib, SC=100;
  const paths=v.base.map(L=>{ let p=L.map(([x,y])=>({X:Math.round(x*SC),Y:Math.round(y*SC)})); if(!C.Clipper.Orientation(p)) p.reverse(); return p; });
  const tol=Math.max(0.03,0.25/traceScale(st))*SC; const co=new C.ClipperOffset(2,tol); co.AddPaths(paths,C.JoinType.jtRound,C.EndType.etClosedPolygon);
  let sol=new C.Paths(); co.Execute(sol,(d+sm)*SC);
  if(sm>0){ // grow past the target then shrink back: rounds every inside corner/notch with radius sm
    const back=new C.ClipperOffset(2,tol); back.AddPaths(sol.filter(p=>C.Clipper.Orientation(p)),C.JoinType.jtRound,C.EndType.etClosedPolygon);
    const s2=new C.Paths(); back.Execute(s2,-sm*SC); sol=s2;
  }
  const out=sol.filter(p=>C.Clipper.Orientation(p)).map(p=>rdp(p.map(q=>[q.X/SC,q.Y/SC]),0.15/traceScale(st))).filter(L=>L.length>=3);
  v.off.set(k,out); return out;
}

function renderItem(it, ppi){
  const st=S.stickers.get(it.stickerId); if(!st) return null;
  const off=effOff(it), paper=S.mode==='paper';
  const line=(S.lineOn&&off>0)?Math.max(S.lineW, 1/ppi):0;
  const key=`${it.stickerId}|${fmt(it.widthIn)}|${off}|${effSm(it)}|${S.mode}|${S.offColor}|${line}|${S.lineC}|${ppi}`;
  if(cache.has(key)) return cache.get(key);
  if(!window.ClipperLib) return renderItemRaster(it,ppi,key);
  const W=Math.max(1,Math.round(it.widthIn*ppi)), H=Math.max(1,Math.round(W*st.h/st.w)), sc=W/st.w;
  const r=off*ppi, lw=line*ppi, pad=Math.ceil(r+4);
  const cw=W+pad*2, ch=H+pad*2;
  const out=mk(cw,ch), ox=out.getContext('2d',{willReadFrequently:true});
  if(r>0.3){
    const spi=st.w/it.widthIn, sm=Math.min(effSm(it), off*4);
    const p=splinePath(offsetLoops(st, off*spi, sm*spi), sc, pad, pad);
    ox.fillStyle=S.offColor; ox.fill(p); var cutPath=p;
    if(lw>0){ ox.save(); ox.clip(p); ox.strokeStyle=S.lineC; ox.lineWidth=lw*2; ox.lineJoin='round'; ox.stroke(p); ox.restore(); }
  }
  // sticker art, trimmed to its own smoothed outline so the art edge is clean too
  const art=mk(cw,ch), ax=art.getContext('2d');
  ax.imageSmoothingEnabled=true; ax.imageSmoothingQuality='high';
  ax.drawImage(st.canvas,pad,pad,W,H);
  const artPath=splinePath(vecOf(st).art, sc, pad, pad);
  ax.globalCompositeOperation='destination-in'; ax.fill(artPath);
  ox.drawImage(art,0,0);
  const res={canvas:out,cw,ch,alpha:null,cut:(r>0.3?cutPath:artPath)};
  if(ppi===PREV){ res.alpha=out.getContext('2d',{willReadFrequently:true}).getImageData(0,0,cw,ch).data; }
  if(cache.size>600) cache.clear();
  cache.set(key,res);
  return res;
}
/* extent of an item (inches) with its rotation */
function itemBox(it){
  const r=renderItem(it,PREV); const a=it.rot*Math.PI/180, c=Math.abs(Math.cos(a)), s=Math.abs(Math.sin(a));
  const w=r.cw/PREV, h=r.ch/PREV; return {w, h, bw:w*c+h*s, bh:w*s+h*c};
}

/* ---------------- nesting: shape-aware bottom-left fill + randomized search ---------------- */
// Each sticker (with its offset) is rasterised to a fine grid once per rotation and cached as row runs.
// A board keeps per-row prefix sums, so "does it fit here?" costs one subtraction per run.
const fpCache=new Map();
function footprint(it, rot){
  const key=`${it.stickerId}|${fmt(it.widthIn)}|${effOff(it)}|${effSm(it)}|${S.mode}|${S.lineOn}|${S.lineW}|${rot}|${S.gap}`;
  const hit=fpCache.get(key); if(hit) return hit;
  const r=renderItem(it,PREV), a=rot*Math.PI/180, c=Math.abs(Math.cos(a)), s=Math.abs(Math.sin(a));
  const bw=Math.ceil(r.cw*c+r.ch*s), bh=Math.ceil(r.cw*s+r.ch*c);
  const t=mk(bw,bh), tx=t.getContext('2d',{willReadFrequently:true});
  tx.translate(bw/2,bh/2); tx.rotate(a); tx.drawImage(r.canvas,-r.cw/2,-r.ch/2);
  const al=tx.getImageData(0,0,bw,bh).data, cell=PREV/GRID;
  const fw=Math.ceil(bw/cell), fh=Math.ceil(bh/cell), g=new Uint8Array(fw*fh);
  for(let y=0;y<bh;y++){ const gy=(y/cell)|0; for(let x=0;x<bw;x++) if(al[(y*bw+x)*4+3]>40) g[gy*fw+((x/cell)|0)]=1; }
  let x0=fw,y0=fh,x1=-1,y1=-1;
  for(let y=0;y<fh;y++) for(let x=0;x<fw;x++) if(g[y*fw+x]){ if(x<x0)x0=x; if(x>x1)x1=x; if(y<y0)y0=y; if(y>y1)y1=y; }
  if(x1<0){ x0=y0=0; x1=fw-1; y1=fh-1; g.fill(1); }
  const w=x1-x0+1, h=y1-y0+1;
  // runs of the shape itself (used for the fit test)
  const runs=[]; let area=0;
  for(let y=0;y<h;y++){ let x=0; while(x<w){ while(x<w&&!g[(y+y0)*fw+x+x0]) x++; if(x>=w) break; const s0=x; while(x<w&&g[(y+y0)*fw+x+x0]) x++; runs.push(y,s0,x-1); area+=x-s0; } }
  // runs of the shape grown by the gap (what gets marked as taken)
  const gc=Math.round(S.gap*GRID), DW=w+gc*2, DH=h+gc*2, D=new Uint8Array(DW*DH);
  if(gc>0){
    const src=new Uint8Array(DW*DH); for(let y=0;y<h;y++) for(let x=0;x<w;x++) if(g[(y+y0)*fw+x+x0]) src[(y+gc)*DW+x+gc]=1;
    const dt=distTo(src,DW,DH), r2=gc*gc+gc; for(let i=0;i<D.length;i++) D[i]=dt[i]<=r2?1:0;
  } else for(let y=0;y<h;y++) for(let x=0;x<w;x++) D[y*DW+x]=g[(y+y0)*fw+x+x0];
  const druns=[];
  for(let y=0;y<DH;y++){ let x=0; while(x<DW){ while(x<DW&&!D[y*DW+x]) x++; if(x>=DW) break; const s0=x; while(x<DW&&D[y*DW+x]) x++; druns.push(y-gc,s0-gc,x-1-gc); } }
  const fp={w,h,area,runs:new Int32Array(runs),druns:new Int32Array(druns),cxIn:(bw/2)/PREV-x0/GRID,cyIn:(bh/2)/PREV-y0/GRID,rot};
  if(fpCache.size>3000) fpCache.clear();
  fpCache.set(key,fp); return fp;
}
function makeBoard(){
  const cols=Math.max(1,Math.floor((S.sheetW-2*S.margin)*GRID)), rows=Math.max(1,Math.floor((S.sheetH-2*S.margin)*GRID));
  return {cols,rows,occ:new Uint8Array(cols*rows),pre:new Int32Array(rows*(cols+1)),bottom:0,right:0};
}
function fits(b,fp,gx,gy){
  const R=fp.runs, P=b.pre, C=b.cols+1;
  for(let k=0;k<R.length;k+=3){ const base=(gy+R[k])*C+gx; if(P[base+R[k+2]+1]-P[base+R[k+1]]) return false; }
  return true;
}
function mark(b,fp,gx,gy){
  const R=fp.druns, C=b.cols, rowsTouched=new Set();
  for(let k=0;k<R.length;k+=3){ const y=gy+R[k]; if(y<0||y>=b.rows) continue;
    const x0=Math.max(0,gx+R[k+1]), x1=Math.min(C-1,gx+R[k+2]); if(x1<x0) continue;
    b.occ.fill(1,y*C+x0,y*C+x1+1); rowsTouched.add(y); }
  for(const y of rowsTouched){ const o=y*C, p=y*(C+1); let s=0; b.pre[p]=0; for(let x=0;x<C;x++){ s+=b.occ[o+x]; b.pre[p+x+1]=s; } }
  b.bottom=Math.max(b.bottom,gy+fp.h); b.right=Math.max(b.right,gx+fp.w);
}
// lowest bottom edge wins, then leftmost — packs tight rows instead of leaving tall gaps
function findSpot(b,it,rots){
  let best=null;
  for(const rot of rots){
    const fp=footprint(it,rot);
    if(fp.w>b.cols||fp.h>b.rows) continue;
    const maxGy=Math.min(b.rows-fp.h, best?best.gy+best.fp.h-fp.h:1e9);
    let found=null;
    for(let gy=0;gy<=maxGy&&!found;gy++){
      const lastGx=b.cols-fp.w;
      for(let gx=0;gx<=lastGx;gx++){
        if(best&&gy+fp.h===best.gy+best.fp.h&&gx>=best.gx) break;
        if(fits(b,fp,gx,gy)){ found={fp,gx,gy}; break; }
      }
    }
    if(found) best=found;
  }
  return best;
}
function setPos(it,spot){ it.rot=spot.fp.rot; it.x=S.margin+spot.gx/GRID+spot.fp.cxIn; it.y=S.margin+spot.gy/GRID+spot.fp.cyIn; }
function place(b,it,rots){ const sp=findSpot(b,it,rots||(S.allowRot?[0,90,180,270]:[it.rot])); if(!sp) return false; mark(b,sp.fp,sp.gx,sp.gy); setPos(it,sp); return true; }
/* mark an already-positioned item onto a board */
function stamp(b,it){
  const fp=footprint(it,it.rot);
  mark(b,fp,Math.round((it.x-fp.cxIn-S.margin)*GRID),Math.round((it.y-fp.cyIn-S.margin)*GRID));
}
function areaOf(it){ return footprint(it,0).area; }
// pack an ordering onto as many sheets as needed; returns placements + a score (lower is better)
function packOrder(order){
  const rotsOf=it=>S.allowRot?[0,90,180,270]:[it.rot];
  const sheets=[]; let queue=order, tooBig=[];
  while(queue.length){
    const b=makeBoard(), placed=[], rest=[];
    for(const it of queue){ const sp=findSpot(b,it,rotsOf(it)); if(sp){ mark(b,sp.fp,sp.gx,sp.gy); placed.push([it,sp]); } else rest.push(it); }
    if(!placed.length){ tooBig=rest; break; }
    sheets.push({placed,b}); queue=rest;
  }
  const last=sheets[sheets.length-1];
  const score=sheets.length*1e9 + (last?last.b.bottom*1e4+last.b.right:0);
  return {sheets,tooBig,score};
}
async function fillCopies(quiet){
  const items=sheet().items; if(!items.length){ if(!quiet) toast('Place at least one sticker first'); return; }
  if(!quiet){ snapshot(); toast('Filling the sheet…',30000); } await tick();
  // keep the current layout exactly as it is; only the empty space gets filled
  const b=makeBoard(); for(const it of items) stamp(b,it);
  const protos=[...new Map(items.map(i=>[i.stickerId+'|'+fmt(i.widthIn)+'|'+i.offset,i])).values()].sort((a,b2)=>areaOf(b2)-areaOf(a));
  const added=[];
  // round-robin through the different stickers so the fill stays varied; stop when a full pass adds nothing
  let more=true;
  while(more && added.length<800){
    more=false;
    for(const p of protos){ const c={...p,id:nid()}; if(place(b,c)){ added.push(c); more=true; } }
    await tick();
  }
  sheet().items=items.concat(added);
  if(quiet){ refresh(); return added.length; }
  track('ss_fill_copies',{ added:added.length, on_sheet:sheet().items.length });
  refresh(); toast(added.length?`Added ${added.length} cop${added.length>1?'ies':'y'}`:'No room for more copies');
}

/* ---------------- placing ---------------- */
function addSticker(stId, opts={}){
  const st=S.stickers.get(stId); if(!st) return;
  const it={id:nid(), stickerId:stId, x:S.sheetW/2, y:S.sheetH/2, widthIn:Math.min(st.natW, S.sheetW-2*S.margin), rot:0, offset:null};
  sheet().items.push(it);
  if(!opts.noPlace){
    // drop it into free space using the current layout as obstacles
    const b=makeBoard();
    for(const o of sheet().items){ if(o===it) continue; stamp(b,o); }
    if(!place(b,it,[0])){ it.x=S.sheetW/2; it.y=S.sheetH/2; toast('No free space — dropped in the middle. Try Auto-arrange.'); }
  }
  return it;
}

/* ---------------- view ---------------- */
const cv=$('#ss-stage'), ctx=cv.getContext('2d'), wrap=$('#ss-wrap');
const V={z:60, ox:0, oy:0, fitZ:60, fitted:false};
let checker=null;
/* The sheet is locked: at 100% it is fitted and centred and nothing moves.
   Zoomed in, you can pan, but the sheet can never leave the view. */
function fitZoom(){ const r=wrap.getBoundingClientRect(), pad=28; return Math.min((r.width-pad*2)/S.sheetW,(r.height-pad*2)/S.sheetH); }
function clampView(){
  const r=wrap.getBoundingClientRect(), sw=S.sheetW*V.z, sh=S.sheetH*V.z, pad=28;
  // smaller than the view on an axis → centre it; bigger → keep its edges from pulling inside the view
  V.ox = sw+pad*2<=r.width  ? (r.width-sw)/2  : clamp(V.ox, r.width-sw-pad, pad);
  V.oy = sh+pad*2<=r.height ? (r.height-sh)/2 : clamp(V.oy, r.height-sh-pad, pad);
}
function zoomTo(z, mx, my){
  const r=wrap.getBoundingClientRect(); if(mx==null){ mx=r.width/2; my=r.height/2; }
  const [ix,iy]=toIn(mx,my);
  V.z=clamp(z, V.fitZ*0.25, V.fitZ*8); V.ox=mx-ix*V.z; V.oy=my-iy*V.z; clampView();
  $('#ss-fit').textContent=Math.round(V.z/V.fitZ*100)+'%'; draw();
}
function fit(){ V.fitZ=fitZoom(); V.z=V.fitZ; clampView(); V.fitted=true; $('#ss-fit').textContent='100%'; draw(); }
function resize(){
  const r=wrap.getBoundingClientRect(), dpr=window.devicePixelRatio||1;
  cv.width=Math.round(r.width*dpr); cv.height=Math.round(r.height*dpr);
  // keep the same relative zoom when the window changes size
  const rel=V.fitted?V.z/V.fitZ:1; V.fitZ=fitZoom(); V.z=V.fitZ*rel; clampView(); V.fitted=true;
  $('#ss-fit').textContent=Math.round(rel*100)+'%'; draw();
}
new ResizeObserver(()=>resize()).observe(wrap);
const toScr=(x,y)=>[V.ox+x*V.z, V.oy+y*V.z];
const toIn=(x,y)=>[(x-V.ox)/V.z,(y-V.oy)/V.z];

function draw(){
  const dpr=window.devicePixelRatio||1;
  ctx.setTransform(dpr,0,0,dpr,0,0);
  const r=wrap.getBoundingClientRect();
  ctx.clearRect(0,0,r.width,r.height);
  const [sx,sy]=toScr(0,0), sw=S.sheetW*V.z, sh=S.sheetH*V.z;
  ctx.save(); ctx.shadowColor='rgba(60,40,20,.18)'; ctx.shadowBlur=18; ctx.shadowOffsetY=4;
  ctx.fillStyle='#fff'; ctx.fillRect(sx,sy,sw,sh); ctx.restore();
  const transparent = S.mode==='cricut' || !S.whiteBg;
  if(transparent){
    if(!checker){ const c=mk(16,16), x=c.getContext('2d'); x.fillStyle='#fff'; x.fillRect(0,0,16,16); x.fillStyle='#F0EBE5'; x.fillRect(0,0,8,8); x.fillRect(8,8,8,8); checker=ctx.createPattern(c,'repeat'); }
    ctx.fillStyle=checker; ctx.fillRect(sx,sy,sw,sh);
  }
  // margin guide
  if(S.margin>0){ ctx.save(); ctx.setLineDash([4,4]); ctx.strokeStyle=ACC_A(.4); ctx.lineWidth=1;
    ctx.strokeRect(sx+S.margin*V.z+.5, sy+S.margin*V.z+.5, sw-2*S.margin*V.z, sh-2*S.margin*V.z); ctx.restore(); }
  ctx.save(); ctx.beginPath(); ctx.rect(sx,sy,sw,sh); ctx.clip();
  ctx.imageSmoothingQuality='high';
  for(const it of sheet().items){
    const rr=renderItem(it,PREV); if(!rr) continue;
    const [cx,cy]=toScr(it.x,it.y), k=V.z/PREV;
    ctx.save(); ctx.translate(cx,cy); ctx.rotate(it.rot*Math.PI/180); ctx.drawImage(rr.canvas,-rr.cw/2*k,-rr.ch/2*k,rr.cw*k,rr.ch*k);
    if(S.showCut&&rr.cut){ ctx.scale(k,k); ctx.translate(-rr.cw/2,-rr.ch/2); ctx.strokeStyle='#E0245E'; ctx.lineWidth=1.25/k; ctx.lineJoin='round'; ctx.stroke(rr.cut); }
    ctx.restore();
  }
  ctx.restore();
  // collisions: red wash over colliding stickers, strong red where they actually overlap
  const col=collisions();
  if(col.overlay){
    const [ox,oy]=toScr(col.x0,col.y0);
    ctx.save(); ctx.imageSmoothingEnabled=true; ctx.drawImage(col.overlay,ox,oy,col.overlay.width/col.R*V.z,col.overlay.height/col.R*V.z); ctx.restore();
  }
  updateWarn(col);
  // several selected: a dashed outline round each, no handles
  if(S.multi.size){ ctx.save(); ctx.strokeStyle=ACC; ctx.lineWidth=1.5; ctx.setLineDash([6,4]);
    for(const it of selItems()){ const c=handles(it).corners; ctx.beginPath(); c.forEach(([x,y],i)=>i?ctx.lineTo(x,y):ctx.moveTo(x,y)); ctx.closePath(); ctx.stroke(); }
    ctx.restore(); }
  // drag-select box
  if(drag&&drag.type==='box'){ const x=Math.min(drag.x0,drag.x1), y=Math.min(drag.y0,drag.y1), w=Math.abs(drag.x1-drag.x0), h=Math.abs(drag.y1-drag.y0);
    ctx.save(); ctx.fillStyle=ACC_A(.08); ctx.fillRect(x,y,w,h); ctx.strokeStyle=ACC; ctx.lineWidth=1; ctx.setLineDash([5,3]); ctx.strokeRect(x+.5,y+.5,w,h); ctx.restore(); }
  // selection
  const s=findItem(S.sel);
  if(s){
    const h=handles(s);
    ctx.save(); ctx.strokeStyle=ACC; ctx.lineWidth=1.5; ctx.setLineDash([6,4]);
    ctx.beginPath(); h.corners.forEach(([x,y],i)=>i?ctx.lineTo(x,y):ctx.moveTo(x,y)); ctx.closePath(); ctx.stroke();
    ctx.setLineDash([]);
    for(const [p,kind] of [[h.rot,'rot'],[h.size,'size']]) drawHandle(p,kind);
    // live size readout under the box: finished size including the offset
    if(drag&&drag.type!=='pan'){ const st=S.stickers.get(s.stickerId), o=effOff(s), W=s.widthIn+2*o, H=s.widthIn*st.h/st.w+2*o;
      const t=`${fmt(W)} × ${fmt(H)} in`, ys=h.corners.map(p=>p[1]), xs=h.corners.map(p=>p[0]);
      ctx.font='600 12px system-ui,sans-serif'; const tw=ctx.measureText(t).width, bx=(Math.min(...xs)+Math.max(...xs))/2-tw/2-7, by=Math.max(...ys)+14;
      ctx.fillStyle='rgba(42,37,33,.88)'; ctx.beginPath(); ctx.roundRect(bx,by,tw+14,21,6); ctx.fill();
      ctx.fillStyle='#fff'; ctx.fillText(t,bx+7,by+15); }
    if(drag&&drag.type==='rot'){ // live angle readout
      const t=`${Math.round(s.rot)}°`; ctx.font='600 12px system-ui'; const tw=ctx.measureText(t).width;
      ctx.fillStyle='rgba(42,37,33,.85)'; ctx.beginPath(); ctx.roundRect(h.rot[0]+16,h.rot[1]-11,tw+12,22,6); ctx.fill();
      ctx.fillStyle='#fff'; ctx.fillText(t,h.rot[0]+22,h.rot[1]+4);
    }
    ctx.restore();
  }
  placeEmpty(sx,sy,sw,sh,r);
}
/* "Your sheet is empty" sits over the part of the sheet that's on screen (not the whole stage),
   so it stays centred and wraps on phones however the view is zoomed. */
function placeEmpty(sx,sy,sw,sh,r){
  const el=$('#ss-empty'), show=!sheet().items.length&&S.sources.length>0&&!loading;
  el.style.display=show?'grid':'none'; if(!show) return;
  const x0=Math.max(0,sx), y0=Math.max(0,sy), x1=Math.min(r.width,sx+sw), y1=Math.min(r.height,sy+sh);
  if(x1-x0<60||y1-y0<60){ el.style.left=el.style.top='0px'; el.style.width=r.width+'px'; el.style.height=r.height+'px'; return; }
  el.style.left=x0+'px'; el.style.top=y0+'px'; el.style.width=(x1-x0)+'px'; el.style.height=(y1-y0)+'px';
}
const HR=11; // handle radius (screen px)
function handles(it){
  const r=renderItem(it,PREV), k=V.z/PREV, a=it.rot*Math.PI/180, c=Math.cos(a), s=Math.sin(a);
  const [cx,cy]=toScr(it.x,it.y), hw=Math.max(r.cw*k/2,HR+4), hh=Math.max(r.ch*k/2,HR+4);
  const P=(x,y)=>[cx+x*c-y*s, cy+x*s+y*c];
  const corners=[P(-hw,-hh),P(hw,-hh),P(hw,hh),P(-hw,hh)];
  // Cricut layout: top-left delete, top-right rotate, bottom-right resize, bottom-left duplicate
  return {corners, del:corners[0], rot:corners[1], size:corners[2], dup:corners[3], c:[cx,cy]};
}
function drawHandle(p,kind){
  const [x,y]=p;
  ctx.save();
  ctx.shadowColor='rgba(0,0,0,.25)'; ctx.shadowBlur=4; ctx.shadowOffsetY=1;
  ctx.fillStyle=kind==='del'?'#C0392B':'#fff'; ctx.beginPath(); ctx.arc(x,y,HR,0,Math.PI*2); ctx.fill();
  ctx.shadowColor='transparent';
  ctx.strokeStyle=kind==='del'?'#C0392B':ACC; ctx.lineWidth=1.5; ctx.stroke();
  ctx.strokeStyle=kind==='del'?'#fff':ACC; ctx.fillStyle=ctx.strokeStyle; ctx.lineWidth=1.8; ctx.lineCap='round'; ctx.lineJoin='round';
  ctx.beginPath();
  if(kind==='del'){ ctx.moveTo(x-4,y-4); ctx.lineTo(x+4,y+4); ctx.moveTo(x+4,y-4); ctx.lineTo(x-4,y+4); ctx.stroke(); }
  if(kind==='rot'){
    // clean circular arrow: 290° clockwise arc + filled arrowhead tangent to its end
    const R=4.6, a0=-Math.PI*0.35, a1=a0+Math.PI*1.55;
    ctx.beginPath(); ctx.arc(x,y,R,a0,a1,false); ctx.stroke();
    const ex=x+R*Math.cos(a1), ey=y+R*Math.sin(a1), tx=-Math.sin(a1), ty=Math.cos(a1), nx=Math.cos(a1), ny=Math.sin(a1), L=3.6, W=2.6;
    ctx.beginPath(); ctx.moveTo(ex+tx*L, ey+ty*L); ctx.lineTo(ex+nx*W-tx*0.6, ey+ny*W-ty*0.6); ctx.lineTo(ex-nx*W-tx*0.6, ey-ny*W-ty*0.6); ctx.closePath(); ctx.fill(); }
  if(kind==='size'){ ctx.moveTo(x-4,y-4); ctx.lineTo(x+4,y+4); ctx.moveTo(x-4,y-4); ctx.lineTo(x-4,y-1); ctx.moveTo(x-4,y-4); ctx.lineTo(x-1,y-4);
    ctx.moveTo(x+4,y+4); ctx.lineTo(x+4,y+1); ctx.moveTo(x+4,y+4); ctx.lineTo(x+1,y+4); ctx.stroke(); }
  if(kind==='dup'){ ctx.strokeRect(x-4.5,y-2,6.5,6.5); ctx.strokeRect(x-2,y-4.5,6.5,6.5); }
  ctx.restore();
}
function handleAt(s,mx,my){
  const h=handles(s); for(const kind of ['rot','size']){ const p=h[kind]; if(Math.hypot(p[0]-mx,p[1]-my)<=HR+3) return {kind,h}; } return null;
}
function hit(mx,my){
  const items=sheet().items;
  for(let n=items.length-1;n>=0;n--){
    const it=items[n], r=renderItem(it,PREV), [cx,cy]=toScr(it.x,it.y), k=V.z/PREV, a=-it.rot*Math.PI/180;
    const dx=mx-cx, dy=my-cy, lx=(dx*Math.cos(a)-dy*Math.sin(a))/k+r.cw/2, ly=(dx*Math.sin(a)+dy*Math.cos(a))/k+r.ch/2;
    if(lx<0||ly<0||lx>=r.cw||ly>=r.ch) continue;
    if(r.alpha[((ly|0)*r.cw+(lx|0))*4+3]>20) return it;
  }
  return null;
}

/* ---------------- collision check ---------------- */
let colCache={key:'',res:{ids:new Set(),pairs:0}};
function collisions(){
  const items=sheet().items, R=90, CLEAR=0.02; // px per inch; shapes closer than CLEAR inches count as touching
  const key=S.cur+'|'+S.sheetW+'x'+S.sheetH+'|'+S.margin+'|'+items.map(i=>[i.id,i.stickerId,i.x.toFixed(3),i.y.toFixed(3),i.rot,fmt(i.widthIn),effOff(i),effSm(i)].join(',')).join(';')+'|'+S.mode+S.lineOn+S.lineW+'|'+S.smooth;
  if(key===colCache.key) return colCache.res;
  const res={ids:new Set(),pairs:0,off:new Set(),overlay:null};
  if(items.length){
    let x0=1e9,y0=1e9,x1=-1e9,y1=-1e9; const bx=items.map(it=>{ const b=itemBox(it); x0=Math.min(x0,it.x-b.bw/2); y0=Math.min(y0,it.y-b.bh/2); x1=Math.max(x1,it.x+b.bw/2); y1=Math.max(y1,it.y+b.bh/2); return b; });
    x0-=0.05; y0-=0.05; x1+=0.05; y1+=0.05;
    const W=Math.ceil((x1-x0)*R)+2, H=Math.ceil((y1-y0)*R)+2;
    if(W*H<=16e6){
      const owner=new Int32Array(W*H).fill(-1), hit=new Uint8Array(W*H), cells=[], pairs=new Set();
      const t=mk(4,4), tx=t.getContext('2d',{willReadFrequently:true}), k=R/PREV;
      items.forEach((it,n)=>{
        const r=renderItem(it,PREV), b=bx[n];
        const ox=Math.floor((it.x-b.bw/2-x0)*R)-3, oy=Math.floor((it.y-b.bh/2-y0)*R)-3;
        const tw=Math.ceil(b.bw*R)+8, th=Math.ceil(b.bh*R)+8;
        t.width=tw; t.height=th; tx.setTransform(1,0,0,1,0,0); tx.clearRect(0,0,tw,th);
        tx.translate((it.x-x0)*R-ox,(it.y-y0)*R-oy); tx.rotate(it.rot*Math.PI/180);
        if(r.cut){ // use the real cut outline, grown by half the clearance, so near-touches are caught too
          tx.scale(k,k); tx.translate(-r.cw/2,-r.ch/2); tx.fillStyle='#000'; tx.fill(r.cut);
          tx.strokeStyle='#000'; tx.lineJoin='round'; tx.lineWidth=CLEAR*PREV; tx.stroke(r.cut);
        } else tx.drawImage(r.canvas,-r.cw*k/2,-r.ch*k/2,r.cw*k,r.ch*k);
        const a=tx.getImageData(0,0,tw,th).data, mine=[];
        for(let y=0;y<th;y++){ const gy=oy+y; if(gy<0||gy>=H) continue;
          for(let x=0;x<tw;x++){ if(a[(y*tw+x)*4+3]<128) continue; const gx=ox+x; if(gx<0||gx>=W) continue;
            const g=gy*W+gx; mine.push(g);
            // off the paper → red too
            const sx=x0+(gx+0.5)/R, sy=y0+(gy+0.5)/R;
            // margin test uses the real cut line: ignore the clearance band added above plus ~1px of rounding
            const m=S.margin-(r.cut?CLEAR/2:0)-1.5/R; if(sx<m||sy<m||sx>S.sheetW-m||sy>S.sheetH-m){ hit[g]=1; res.off.add(it.id); }
            const o=owner[g];
            if(o<0) owner[g]=n;
            else if(o!==n){ hit[g]=1; res.ids.add(it.id); res.ids.add(items[o].id); pairs.add(Math.min(o,n)+'-'+Math.max(o,n)); }
          } }
        cells.push(mine);
      });
      res.pairs=pairs.size;
      for(const id of res.off) res.ids.add(id);
      if(res.ids.size){
        const ov=mk(W,H), ox2=ov.getContext('2d'), id=ox2.createImageData(W,H), d=id.data;
        items.forEach((it,n)=>{ if(!res.ids.has(it.id)) return; for(const g of cells[n]){ const q=g*4; d[q]=220; d[q+1]=38; d[q+2]=38; if(d[q+3]<70) d[q+3]=70; } });
        for(let g=0;g<hit.length;g++) if(hit[g]){ const q=g*4; d[q]=230; d[q+1]=20; d[q+2]=20; d[q+3]=200; }
        ox2.putImageData(id,0,0);
        res.overlay=ov; res.x0=x0; res.y0=y0; res.R=R;
      }
    }
  }
  colCache={key,res};
  return res;
}
function updateWarn(col){
  const w=$('#ss-warn'); const n=col.ids.size;
  const parts=[]; if(col.pairs) parts.push(`${col.pairs} overlap${col.pairs>1?'s':''}`); if(col.off.size) parts.push(`${col.off.size} past the margin`);
  w.hidden=!n; if(n) w.textContent='⚠ '+parts.join(' · ');
}
$('#ss-warn').onclick=()=>{ const col=collisions(); const it=sheet().items.find(i=>col.ids.has(i.id)); if(it){ S.sel=it.id; renderSel(); draw(); } };

/* pointer */
let drag=null;
cv.addEventListener('pointerdown',e=>{
  const rc=cv.getBoundingClientRect(), mx=e.clientX-rc.left, my=e.clientY-rc.top;
  cv.setPointerCapture(e.pointerId);
  const s=findItem(S.sel);
  if(s){
    const hh=handleAt(s,mx,my);
    if(hh){ const h=hh.h;
      if(hh.kind==='del'){ drag=null; delSel(); return; }
      if(hh.kind==='dup'){ drag=null; dupSel(); return; }
      if(hh.kind==='size'){ snapshot(); drag={type:'size',it:s,w0:s.widthIn,d0:Math.hypot(mx-h.c[0],my-h.c[1])}; return; }
      if(hh.kind==='rot'){ snapshot(); drag={type:'rot',it:s,c:h.c,r0:s.rot,a0:Math.atan2(my-h.c[1],mx-h.c[0])}; draw(); return; }
    }
  }
  const it=hit(mx,my);
  // Shift+click adds or removes a sticker from the selection
  if(it&&e.shiftKey){ const ids=new Set(selItems().map(i=>i.id)); ids.has(it.id)?ids.delete(it.id):ids.add(it.id); setSelection(ids); drag=null; renderSel(); draw(); return; }
  // several selected: dragging any of them moves them all
  if(it&&S.multi.has(it.id)){ snapshot(); const [ix,iy]=toIn(mx,my);
    drag={type:'moveMany',items:selItems().map(i=>({it:i,dx:i.x-ix,dy:i.y-iy})),moved:false}; draw(); return; }
  if(it){
    S.multi.clear(); S.sel=it.id; snapshot();
    const [ix,iy]=toIn(mx,my); drag={type:'move',it,dx:it.x-ix,dy:it.y-iy,moved:false};
    // bring to front
    const arr=sheet().items; arr.splice(arr.indexOf(it),1); arr.push(it);
  } else {
    // empty space: drag a box to select. Pan instead with Space / middle button held,
    // or with a finger when zoomed in (mouse users pan zoomed sheets with the scroll wheel).
    S.sel=null; S.multi.clear();
    const zoomed=V.z>V.fitZ*1.001, panning=spaceDown||e.button===1||(zoomed&&e.pointerType!=='mouse');
    drag=panning?(zoomed?{type:'pan',x:mx,y:my,ox:V.ox,oy:V.oy}:null):{type:'box',x0:mx,y0:my,x1:mx,y1:my};
  }
  renderSel(); draw();
});
cv.addEventListener('pointermove',e=>{
  const rc=cv.getBoundingClientRect(), mx=e.clientX-rc.left, my=e.clientY-rc.top;
  if(!drag){
    const s=findItem(S.sel); let cur='default';
    if(s){ const hh=handleAt(s,mx,my); if(hh) cur={del:'pointer',dup:'copy',size:'nwse-resize',rot:'grab'}[hh.kind]; }
    if(cur==='default' && hit(mx,my)) cur='move';
    else if(cur==='default' && V.z>V.fitZ*1.001) cur='grab';
    cv.style.cursor=cur; return;
  }
  if(drag.type==='pan'){ V.ox=drag.ox+mx-drag.x; V.oy=drag.oy+my-drag.y; clampView(); draw(); return; }
  if(drag.type==='box'){ drag.x1=mx; drag.y1=my; draw(); return; }
  if(drag.type==='moveMany'){ const [ix,iy]=toIn(mx,my); for(const m of drag.items){ m.it.x=ix+m.dx; m.it.y=iy+m.dy; } drag.moved=true; draw(); return; }
  const it=drag.it;
  if(drag.type==='move'){ const [ix,iy]=toIn(mx,my); it.x=ix+drag.dx; it.y=iy+drag.dy; drag.moved=true; }
  if(drag.type==='size'){ const h=toScr(it.x,it.y); const dd=Math.hypot(mx-h[0],my-h[1]); it.widthIn=clamp(drag.w0*dd/Math.max(1,drag.d0),0.2,Math.max(S.sheetW,S.sheetH)); renderSel(); }
  if(drag.type==='rot'){ let a=drag.r0+(Math.atan2(my-drag.c[1],mx-drag.c[0])-drag.a0)*180/Math.PI; if(e.shiftKey) a=Math.round(a/15)*15; it.rot=((Math.round(a)%360)+360)%360; renderSel(); }
  draw();
});
const endDrag=()=>{ if(drag&&(drag.type==='move'||drag.type==='moveMany')&&!drag.moved) undoStack.pop(); const was=drag; drag=null;
  if(was&&was.type==='box'){ boxSelect(was); return; }
  if(was&&was.type!=='pan'){ renderLib(); draw(); } };   // draw: drop the live size readout
// select every sticker whose outline box touches the dragged box (screen px)
function boxSelect(b){
  const x0=Math.min(b.x0,b.x1), x1=Math.max(b.x0,b.x1), y0=Math.min(b.y0,b.y1), y1=Math.max(b.y0,b.y1);
  if(x1-x0<4&&y1-y0<4){ renderSel(); draw(); return; }   // a plain click on empty space just clears the selection
  const ids=sheet().items.filter(it=>{ const c=handles(it).corners, xs=c.map(p=>p[0]), ys=c.map(p=>p[1]);
    return Math.max(...xs)>=x0&&Math.min(...xs)<=x1&&Math.max(...ys)>=y0&&Math.min(...ys)<=y1; }).map(it=>it.id);
  setSelection(ids); if(ids.length>1) track('ss_box_select',{ stickers:ids.length });
  renderSel(); draw();
}
let spaceDown=false;
window.addEventListener('keyup',e=>{ if(e.code==='Space') spaceDown=false; });
window.addEventListener('blur',()=>{ spaceDown=false; });
cv.addEventListener('pointerup',endDrag); cv.addEventListener('pointercancel',endDrag);
// plain scroll = page scroll (pans the sheet when zoomed in); Ctrl/⌘ + scroll (or trackpad pinch) = zoom at the cursor
cv.addEventListener('wheel',e=>{
  if(!(e.ctrlKey||e.metaKey)){   // zoomed in: plain scroll pans the sheet
    if(V.z<=V.fitZ*1.001) return;
    e.preventDefault(); V.ox-=e.deltaX; V.oy-=e.deltaY; clampView(); draw(); return; }
  e.preventDefault();
  const rc=cv.getBoundingClientRect(); zoomTo(V.z*Math.exp(-e.deltaY*0.004), e.clientX-rc.left, e.clientY-rc.top);
},{passive:false});
$('#ss-zIn').onclick=()=>zoomTo(V.z*1.25); $('#ss-zOut').onclick=()=>zoomTo(V.z/1.25);

window.addEventListener('keydown',e=>{
  if(/INPUT|SELECT|TEXTAREA/.test(document.activeElement?.tagName)) return;
  if(document.querySelector('.bt-proc:not([hidden])')) return;   // download popup is open
  const mod=e.ctrlKey||e.metaKey;
  if(mod&&e.key.toLowerCase()==='z'){ e.preventDefault(); return undo(); }
  if(e.code==='Space'&&!e.repeat&&e.target===document.body){ spaceDown=true; e.preventDefault(); }
  if(mod&&e.key.toLowerCase()==='a'&&sheet().items.length&&(document.activeElement===document.body||cv.contains(document.activeElement))){ e.preventDefault(); setSelection(sheet().items.map(i=>i.id)); return refresh(); }
  const sel=selItems(); if(!sel.length) return;
  if(e.key==='Delete'||e.key==='Backspace'){ e.preventDefault(); delSel(); }
  else if(mod&&e.key.toLowerCase()==='d'){ e.preventDefault(); dupSel(); }
  else if(mod&&e.key.toLowerCase()==='j'){ e.preventDefault(); joinSel(); }
  else if(!mod&&(e.key==='r'||e.key==='R')){ snapshot(); sel.forEach(s=>s.rot=(s.rot+90)%360); refresh(); }
  else if(e.key==='Escape'){ S.sel=null; S.multi.clear(); refresh(); }
  else if(e.key.startsWith('Arrow')){
    e.preventDefault(); const st=e.shiftKey?0.1:0.01;
    for(const s of sel){ if(e.key==='ArrowLeft')s.x-=st; if(e.key==='ArrowRight')s.x+=st; if(e.key==='ArrowUp')s.y-=st; if(e.key==='ArrowDown')s.y+=st; } draw();
  }
});
function delSel(){ const sel=selItems(); if(!sel.length) return; snapshot(); const del=new Set(sel); sheet().items=sheet().items.filter(i=>!del.has(i)); S.sel=null; S.multi.clear(); refresh(); }
function dupSel(){ const sel=selItems(); if(!sel.length) return; snapshot(); const copies=sel.map(s=>({...s,id:nid(),x:s.x+0.2,y:s.y+0.2})); sheet().items.push(...copies); setSelection(copies.map(c=>c.id)); refresh(); }
/* Join: the selected stickers become ONE sticker, drawn exactly as they sit on the sheet (size,
   rotation, overlap), with one shared offset outline. The originals stay in step 1's list. */
function joinSel(){
  const sel=selItems(); if(sel.length<2) return toast('Select two or more stickers to join (drag a box, or Shift+click)');
  snapshot();
  // resolution: the sharpest of the joined pieces, capped so the new image stays a sane size
  let ppi=Math.max(...sel.map(it=>S.stickers.get(it.stickerId).w/it.widthIn));
  const parts=sel.map(it=>{ const st=S.stickers.get(it.stickerId), w=it.widthIn, h=it.widthIn*st.h/st.w, a=it.rot*Math.PI/180, c=Math.abs(Math.cos(a)), s=Math.abs(Math.sin(a));
    return {it,st,w,h,a,bw:w*c+h*s,bh:w*s+h*c}; });
  const X0=Math.min(...parts.map(p=>p.it.x-p.bw/2)), X1=Math.max(...parts.map(p=>p.it.x+p.bw/2)),
        Y0=Math.min(...parts.map(p=>p.it.y-p.bh/2)), Y1=Math.max(...parts.map(p=>p.it.y+p.bh/2));
  ppi=Math.min(ppi, 3000/Math.max(X1-X0,Y1-Y0));
  const P=2, W=Math.ceil((X1-X0)*ppi)+P*2, H=Math.ceil((Y1-Y0)*ppi)+P*2, c=mk(W,H), x=c.getContext('2d',{willReadFrequently:true});
  x.imageSmoothingEnabled=true; x.imageSmoothingQuality='high';
  for(const p of parts.sort((a,b)=>sheet().items.indexOf(a.it)-sheet().items.indexOf(b.it))){
    x.save(); x.translate(P+(p.it.x-X0)*ppi, P+(p.it.y-Y0)*ppi); x.rotate(p.a); x.drawImage(p.st.canvas,-p.w*ppi/2,-p.h*ppi/2,p.w*ppi,p.h*ppi); x.restore(); }
  const st={id:nid(), srcId:null, name:'Joined sticker', canvas:c, w:W, h:H, bbox:[0,0,W,H], natW:W/ppi, thumb:null, fromSplit:true, joined:true};
  S.stickers.set(st.id,st); try{ vecOf(st); }catch(e){}
  const first=sel[0], items=sheet().items, at=Math.max(...sel.map(i=>items.indexOf(i)));
  const it={id:nid(), stickerId:st.id, x:(X0+X1)/2, y:(Y0+Y1)/2, widthIn:W/ppi, rot:0, offset:first.offset, smooth:first.smooth};
  items.splice(at+1,0,it); const gone=new Set(sel); sheet().items=items.filter(i=>!gone.has(i));
  S.multi.clear(); S.sel=it.id;
  track('ss_join',{ stickers:sel.length }); refresh(); toast(`Joined ${sel.length} stickers into one`);
}

/* ---------------- UI rendering ---------------- */
/* The uploaded images aren't listed: their stickers show in the grid below, each with its own ×. */
function renderSources(){
  $('#ss-drop').hidden=S.sources.length>0;
  $('#ss-meta').hidden=S.stickers.size>0;
  const op=S.sources.filter(x=>x.opaque), box=$('#ss-bgMode'); if(!box) return;
  box.hidden=!op.length;
  const keep=op.filter(x=>x.photo).length, mixed=keep>0&&keep<op.length;
  box.querySelector('[data-bg="cut"]').setAttribute('aria-pressed', !mixed&&keep===0);
  box.querySelector('[data-bg="keep"]').setAttribute('aria-pressed', !mixed&&keep===op.length&&op.length>0);
  $('#ss-bgHint').textContent=mixed?'Some images are cut out, some kept whole. Tap a sticker to change just that image.':
    keep?'Whole images, cropped to a shape. Tap one on the sheet to pick circle, heart and more.':'Background removed. Tap a sticker on the sheet to keep just that image whole.';
}
function removeSource(s){
  const ids=new Set([...S.stickers.values()].filter(x=>x.srcId===s.id).map(x=>x.id));
  snapshot();
  const idx=S.sources.indexOf(s); grave.sources.set(s.id,{src:s,idx}); S.sources.splice(idx,1);
  ids.forEach(id=>{ grave.stickers.set(id,S.stickers.get(id)); S.stickers.delete(id); });
  S.sheets.forEach(sh=>sh.items=sh.items.filter(i=>!ids.has(i.stickerId)));
  renderSources(); renderLib(); refresh(); toast(`Removed “${s.name}” — Ctrl+Z to undo`);
}
// shadow at the bottom of the sticker grid while there are more stickers below
function libMore(){ const l=$('#ss-lib'); l.classList.toggle('is-more', l.scrollHeight-l.scrollTop-l.clientHeight>4); }
$('#ss-lib').addEventListener('scroll',libMore,{passive:true}); new ResizeObserver(libMore).observe($('#ss-lib'));
function renderLib(){
  const lib=$('#ss-lib'); lib.innerHTML='';
  const counts=new Map(); S.sheets.forEach(sh=>sh.items.forEach(i=>counts.set(i.stickerId,(counts.get(i.stickerId)||0)+1)));
  for(const st of S.stickers.values()){
    if(!st.thumb){ const k=Math.min(1,240/Math.max(st.w,st.h)), t=mk(st.w*k,st.h*k), tx=t.getContext('2d'); tx.imageSmoothingQuality='high'; tx.drawImage(st.canvas,0,0,t.width,t.height); st.thumb=t.toDataURL(); }
    const d=document.createElement('div'); d.className='lib__it'; d.title=`${st.name} — tap to add another`;
    d.innerHTML=`<img alt="">${st.photo?'<button type="button" class="lib__cut" title="Move and zoom the photo inside its shape">✥ Adjust</button>':`<button type="button" class="lib__cut" title="Split into separate stickers">${ICO_SPLIT}Split</button>`}<button type="button" class="lib__x" title="Remove sticker" aria-label="Remove sticker">×</button>`+(counts.get(st.id)?`<span class="lib__n">${counts.get(st.id)}</span>`:'');
    d.querySelector('img').src=st.thumb;
    const cut=d.querySelector('.lib__cut'); if(cut) cut.onclick=e=>{ e.stopPropagation(); st.photo?openCrop(st.id):openSplit(st.id); };
    d.onclick=e=>{ if(e.target.closest('.lib__x')||e.target.closest('.lib__cut')) return; snapshot(); const it=addSticker(st.id); S.sel=it?.id; refresh(); notePlaced('click'); };
    d.querySelector('.lib__x').onclick=()=>{
      snapshot(); grave.stickers.set(st.id,st); S.stickers.delete(st.id); S.sheets.forEach(sh=>sh.items=sh.items.filter(i=>i.stickerId!==st.id));
      renderSources(); renderLib(); refresh();
    };
    lib.appendChild(d);
  }
  lib.classList.toggle('is-many',S.stickers.size>6); libMore();
  $('#ss-libCount').textContent=S.stickers.size;
  $('#ss-addAll').disabled=!S.stickers.size;
}
// selected-sticker controls: left side of the canvas top bar (#ss-sel), never over the art.
// Nothing selected = a hint in the same slot, so the bar (and the stage under it) never changes height.
const ICO=p=>`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${p}</svg>`;
const ICO_DUP=ICO('<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V6a2 2 0 0 1 2-2h8"/>');
// split = one shape coming apart into two halves; join = two shapes overlapping into one
const ICO_SPLIT=ICO('<path d="M14.95 5.05A7 7 0 0 0 5.05 14.95z"/><path d="M9.05 18.95A7 7 0 0 0 18.95 9.05z"/><path d="M4.5 19.5 19.5 4.5" stroke-width="1.5"/>');
const ICO_JOIN=ICO('<circle cx="9" cy="12" r="6"/><circle cx="15" cy="12" r="6"/>');
const ICO_ADJ=ICO('<path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3"/>');
const ICO_DEL=ICO('<path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"/>');
function renderSel(){
  const el=$('#ss-sel');
  if(S.sel) S.multi.clear(); else if(S.multi.size){ setSelection(selItems().map(i=>i.id)); }
  const s=findItem(S.sel), many=S.multi.size>1;
  $('#ss-bar').toggleAttribute('data-sel',!!s||many);
  if(many) return renderMulti(el);
  if(!s){ const k=sheet().items.length?'hint':'none'; if(el.dataset.for!==k){ el.dataset.for=k;
    el.innerHTML=k==='hint'?'<span class="ss-sel__hint">Tap a sticker to edit it, or drag a box to select several.</span>':''; } return; }
  const stOf=S.stickers.get(s.stickerId), srcOf=stOf&&S.sources.find(x=>x.id===stOf.srcId), isPhoto=!!(stOf&&stOf.photo);
  const forKey=s.id+':'+(isPhoto?stOf.photo:'c');
  if(el.dataset.for!==forKey){
    el.dataset.for=forKey;
    // one Shape menu: "Cut out" (only for images with a background to remove) plus the photo shapes
    const canCut=srcOf&&srcOf.opaque, shapeMenu=isPhoto||canCut;
    el.innerHTML=`
      <div class="ss-sel__a">
        <div class="ss-f ss-f--size"><span>Size</span><input id="ss-pW" type="number" step="0.05" min="0.2" aria-label="Width in inches" title="Width (in), with the offset"><i>×</i><input id="ss-pH" type="number" step="0.05" min="0.2" aria-label="Height in inches" title="Height (in), with the offset"><i>in</i></div>
        <div class="ss-sel__acts">
          <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon" id="ss-pDup" title="Duplicate (Ctrl/⌘+D)" aria-label="Duplicate">${ICO_DUP}</button>
          ${isPhoto?`<button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon" id="ss-pCrop" title="Move and zoom the photo inside its shape" aria-label="Adjust photo">${ICO_ADJ}</button>`
                   :`<button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon" id="ss-pSplit" title="Split into separate stickers" aria-label="Split">${ICO_SPLIT}</button>`}
          <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon ss-danger" id="ss-pDel" title="Delete (Del)" aria-label="Delete">${ICO_DEL}</button>
        </div>
      </div>
      <div class="ss-sel__b">
        ${shapeMenu?`<label class="ss-f ss-f--shape" title="Cut the sticker out, or keep the whole image in a shape"><span>Shape</span><select id="ss-pShape" aria-label="Shape">${canCut?`<option value="cut"${isPhoto?'':' selected'}>✂ Cut out</option>`:''}${SHAPES.map(([k,n])=>`<option value="${k}"${isPhoto&&stOf.photo===k?' selected':''}>${n}</option>`).join('')}</select></label>`:''}
        ${OFF_ROW}
      </div>`;
    bindOffsetSliders(el);
    // typed size = finished size including the offset; ratio stays locked
    const sizeIn=(id,fn)=>{ const i=el.querySelector(id); i.addEventListener('focus',snapshot);
      const go=()=>{ const it=findItem(S.sel), v=parseFloat(i.value); if(!it||!isFinite(v)||v<=0) return; fn(it,v); draw(); syncSel(); };
      i.addEventListener('change',go); i.addEventListener('keydown',e=>{ if(e.key==='Enter'){ go(); i.blur(); } e.stopPropagation(); }); };
    sizeIn('#ss-pW',(it,v)=>{ it.widthIn=clamp(v-2*effOff(it),0.2,40); });
    sizeIn('#ss-pH',(it,v)=>{ const st=S.stickers.get(it.stickerId); it.widthIn=clamp((v-2*effOff(it))*st.w/st.h,0.2,40); });
    el.querySelector('#ss-pDup').onclick=dupSel;
    el.querySelector('#ss-pDel').onclick=delSel;
    const spl=el.querySelector('#ss-pSplit'); if(spl) spl.onclick=()=>{ const it=findItem(S.sel); if(it) openSplit(it.stickerId); };
    const crb=el.querySelector('#ss-pCrop'); if(crb) crb.onclick=()=>{ const it=findItem(S.sel); if(it) openCrop(it.stickerId); };
    const shp=el.querySelector('#ss-pShape');
    if(shp) shp.onchange=()=>{ const v=shp.value;
      if(v==='cut'){ if(isPhoto&&srcOf) switchKind(srcOf,false); }
      else if(!isPhoto){ if(srcOf){ photoShape=v; switchKind(srcOf,true); } }
      else { const it=findItem(S.sel); if(it) setShape(it.stickerId,v); } };
  }
  syncSel();
}
// offset / smooth sliders + ↺: change every selected sticker
const OFF_ROW=`
        <label class="ss-f ss-f--rng ss-f--off"><span>Offset</span><input type="range" id="ss-pO" min="0" max="0.5" step="0.01"><b id="ss-pOv"></b></label>
        <label class="ss-f ss-f--rng ss-f--sm"><span>Smooth</span><input type="range" id="ss-pSm" min="0" max="0.3" step="0.01"><b id="ss-pSmv"></b></label>
        <button type="button" id="ss-pReset" class="ss-reset" title="Match all stickers (use the offset from step 2)" aria-label="Match all stickers">↺</button>`;
function bindOffsetSliders(el){
  const live=(id,fn)=>{ const i=el.querySelector(id); i.addEventListener('pointerdown',snapshot); i.addEventListener('keydown',snapshot);
    i.addEventListener('input',()=>{ const v=parseFloat(i.value)||0; selItems().forEach(it=>fn(it,v)); draw(); syncSel(); }); };
  live('#ss-pO',(it,v)=>{ it.offset=clamp(v,0,0.5); });
  live('#ss-pSm',(it,v)=>{ it.smooth=clamp(v,0,0.3); });
  el.querySelector('#ss-pReset').onclick=()=>{ snapshot(); selItems().forEach(it=>{ it.offset=null; it.smooth=null; }); draw(); syncSel(); };
}
// 2+ stickers selected: count, offset / smooth for all of them, duplicate / join / delete
function renderMulti(el){
  const n=S.multi.size, k='m'+n;
  if(el.dataset.for!==k){ el.dataset.for=k;
    el.innerHTML=`
      <div class="ss-sel__a">
        <div class="ss-f ss-f--count"><b>${n} selected</b></div>
        <div class="ss-sel__acts">
          <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon" id="ss-pDup" title="Duplicate (Ctrl/⌘+D)" aria-label="Duplicate">${ICO_DUP}</button>
          <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon" id="ss-pJoin" title="Join into one sticker (Ctrl/⌘+J)" aria-label="Join into one sticker">${ICO_JOIN}</button>
          <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm bt-btn--icon ss-danger" id="ss-pDel" title="Delete (Del)" aria-label="Delete">${ICO_DEL}</button>
        </div>
      </div>
      <div class="ss-sel__b">${OFF_ROW}
      </div>`;
    bindOffsetSliders(el);
    el.querySelector('#ss-pDup').onclick=dupSel; el.querySelector('#ss-pJoin').onclick=joinSel; el.querySelector('#ss-pDel').onclick=delSel;
  }
  syncSel();
}
function syncSel(){
  const el=$('#ss-sel'), sel=selItems(), s=sel[0]; if(!s||!el.querySelector('#ss-pO')) return;
  const st=S.stickers.get(s.stickerId), set=(id,v)=>{ const i=el.querySelector(id); if(i&&document.activeElement!==i) i.value=v; };
  const own=sel.some(i=>i.offset!=null||i.smooth!=null); set('#ss-pO',effOff(s)); set('#ss-pSm',effSm(s));
  if(el.querySelector('#ss-pW')){ set('#ss-pW',fmt(s.widthIn+2*effOff(s))); set('#ss-pH',fmt(s.widthIn*st.h/st.w+2*effOff(s))); }
  el.querySelector('#ss-pReset').style.visibility=own?'':'hidden';   // keeps its space, so the sliders don't shift
  el.querySelector('#ss-pOv').textContent=fmt(effOff(s))+'"';
  el.querySelector('#ss-pSmv').textContent=fmt(effSm(s))+'"';
}
function updateStatus(){}
function refresh(){ renderSel(); draw(); renderLib(); sizeHint(); }
function sizeHint(){
  const b=contentBox(sheet()), w=S.trim&&b?b.w:S.sheetW, h=S.trim&&b?b.h:S.sheetH;
  $('#ss-sizeHint').innerHTML = !sheet().items.length ? '' :
    S.mode==='cricut'
      ? `300 DPI PNG, ${fmt(w)} × ${fmt(h)} in. Some cutting software ignores the DPI in the file, so after uploading set <b>W = ${fmt(w)} in</b> (lock ratio on) — the width is also in the file name.`
      : `This sheet exports at ${fmt(w)} × ${fmt(h)} in.`;
}

/* ---------------- export ---------------- */
function contentBox(sh){
  if(!sh.items.length) return null;
  let x0=1e9,y0=1e9,x1=-1e9,y1=-1e9;
  for(const it of sh.items){ const b=itemBox(it); x0=Math.min(x0,it.x-b.bw/2); y0=Math.min(y0,it.y-b.bh/2); x1=Math.max(x1,it.x+b.bw/2); y1=Math.max(y1,it.y+b.bh/2); }
  x0=Math.max(0,x0); y0=Math.max(0,y0); x1=Math.min(S.sheetW,x1); y1=Math.min(S.sheetH,y1);
  return {x:x0,y:y0,w:x1-x0,h:y1-y0};
}
function renderSheet(sh, dpi){
  let box={x:0,y:0,w:S.sheetW,h:S.sheetH};
  if(S.trim){ const b=contentBox(sh); if(b) box=b; }
  dpi=fitDpi(box.w,box.h,dpi);
  const W=Math.round(box.w*dpi), H=Math.round(box.h*dpi);
  if(W*H>MAX_PX) throw new Error('Too large — lower the DPI');
  const c=mk(W,H), x=c.getContext('2d');
  if(S.mode==='paper'&&S.whiteBg){ x.fillStyle='#fff'; x.fillRect(0,0,W,H); }
  x.imageSmoothingQuality='high';
  for(const it of sh.items){
    const r=renderItem(it,dpi);
    x.save(); x.translate((it.x-box.x)*dpi,(it.y-box.y)*dpi); x.rotate(it.rot*Math.PI/180); x.drawImage(r.canvas,-r.cw/2,-r.ch/2); x.restore();
  }
  // don't keep huge export renders in the cache
  for(const k of [...cache.keys()]) if(!k.endsWith('|'+PREV)) cache.delete(k);
  return {canvas:c, w:box.w, h:box.h, dpi};
}
/* PNG with a pHYs chunk so apps that read DPI get the true size */
const CRC=(()=>{ const t=new Uint32Array(256); for(let n=0;n<256;n++){ let c=n; for(let k=0;k<8;k++) c=c&1?0xEDB88320^(c>>>1):c>>>1; t[n]=c>>>0; } return t; })();
function crc32(b){ let c=0xFFFFFFFF; for(let i=0;i<b.length;i++) c=CRC[(c^b[i])&255]^(c>>>8); return (c^0xFFFFFFFF)>>>0; }
async function pngBlob(canvas,dpi){
  const blob=await new Promise(r=>canvas.toBlob(r,'image/png'));
  const buf=new Uint8Array(await blob.arrayBuffer()), ppm=Math.round(dpi/0.0254);
  const ch=new Uint8Array(21), dv=new DataView(ch.buffer);
  dv.setUint32(0,9); ch.set([112,72,89,115],4); dv.setUint32(8,ppm); dv.setUint32(12,ppm); ch[16]=1; dv.setUint32(17,crc32(ch.subarray(4,17)));
  const out=new Uint8Array(buf.length+21); out.set(buf.subarray(0,33)); out.set(ch,33); out.set(buf.subarray(33),54);
  return new Blob([out],{type:'image/png'});
}
function save(blob,name){ const a=document.createElement('a'); a.href=URL.createObjectURL(blob); a.download=(window.BT && BT.brandName ? BT.brandName(name) : name); document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(a.href),4000); }
async function downloadSheet(i){
  const sh=S.sheets[i]; if(!sh.items.length) return false;
  const r=renderSheet(sh,S.dpi), blob=await pngBlob(r.canvas,r.dpi);
  save(blob,`sticker-sheet - ${fmt(r.w)}x${fmt(r.h)}in - ${S.mode==='cricut'?'print-and-cut':'paper'} - BA.png`);
  return r;
}
function overlapNote(){ const c=collisions(); if(!c.ids.size) return ''; const p=[]; if(c.pairs) p.push(`${c.pairs} overlap${c.pairs>1?'s':''}`); if(c.off.size) p.push(`${c.off.size} past the margin`); return ' · ⚠ '+p.join(', '); }
/* ---------- downloads: the shared popup in the core (BT.runWithProgress) ---------- */
function setDownloads(on){ $('#ss-dlOne').disabled=$('#ss-dlZip').disabled=!on; }
function runWithProgress(stages, work){
  if(document.querySelector('.bt-proc:not([hidden])')) return;   // popup already open
  setDownloads(false);
  if(window.BT&&typeof window.BT.runWithProgress==='function'){
    window.BT.runWithProgress({
      stages,
      gateText:'Your file is almost ready! Enter your email to get it.',
      doneText:'Your file is ready!',
      errorText:'Something went wrong making your file. Try again, or try a lower resolution.',
      work,
      onEnd(){ setDownloads(true); }
    });
    return;
  }
  // old core cached: no popup, just make the file
  Promise.resolve().then(()=>work({ gated:false })).catch(e=>{ console.error('[Sticker Sheet Maker]',e); toast('Something went wrong making your file. Try again, or try a lower resolution.'); })
    .then(()=>setDownloads(true));
}

function dlProps(kind,extra){ const c=collisions(); return Object.assign({ kind, demo:!!S.demo, sheet:S.mode==='cricut'?'print_then_cut':`${S.sheetW}x${S.sheetH}`, stickers_on_sheet:sheet().items.length,
  stickers_found:S.stickers.size, dpi:S.dpi, offset_in:S.offset, outline:!!S.lineOn, overlaps:c.pairs, past_margin:c.off.size }, extra||{}); }
$('#ss-dlOne').onclick=()=>{
  if(!sheet().items.length) return toast('Add some stickers to the sheet first');
  const n=sheet().items.length;
  runWithProgress([{p:25,wait:350,text:'Gathering your stickers…'},{p:60,wait:800,text:`Cutting smooth offsets for ${n} sticker${n>1?'s':''}…`},{p:85,wait:750,text:`Rendering at ${S.dpi} DPI…`}], async()=>{
    await tick(); const r=await downloadSheet(S.cur);
    const props=dlProps('sheet',{ width_in:+fmt(r.w), height_in:+fmt(r.h) });
    track('ss_download_click',props); klEvent('Used Sticker Sheet Maker',props);
    toast(`Downloaded — ${fmt(r.w)} × ${fmt(r.h)} in at ${r.dpi} DPI`+(r.dpi<S.dpi?' (the most this device can make)':'')+overlapNote(),5000);
  });
};
$('#ss-dlZip').onclick=()=>{
  const items=sheet().items.length?sheet().items:[...S.stickers.values()].map(st=>({id:0,stickerId:st.id,x:0,y:0,widthIn:st.natW,rot:0,offset:null}));
  if(!items.length) return toast('No stickers yet — add a sticker sheet first');
  if(!window.JSZip) return toast('The ZIP maker didn’t load. Refresh the page and try again.');
  runWithProgress([{p:25,wait:350,text:'Gathering your stickers…'},{p:60,wait:800,text:'Cutting each sticker out…'},{p:85,wait:750,text:'Packing your ZIP…'}], async()=>{
    await tick();
    const zip=new JSZip(), seen=new Map(); let lowered=false;
    for(const it of items){
      const key=it.stickerId+'|'+fmt(it.widthIn)+'|'+effOff(it)+'|'+effSm(it)+'|'+it.rot; if(seen.has(key)) continue; seen.set(key,1);
      const st=S.stickers.get(it.stickerId), pad=2*effOff(it)+0.1, d=fitDpi(1.42*(it.widthIn+pad),1.42*(it.widthIn*st.h/st.w+pad),S.dpi); if(d<S.dpi) lowered=true;
      const r=renderItem(it,d), a=it.rot*Math.PI/180, c=Math.abs(Math.cos(a)), s=Math.abs(Math.sin(a));
      const W=Math.ceil(r.cw*c+r.ch*s), H=Math.ceil(r.cw*s+r.ch*c), cv2=mk(W,H), x=cv2.getContext('2d');
      x.translate(W/2,H/2); x.rotate(a); x.drawImage(r.canvas,-r.cw/2,-r.ch/2);
      zip.file((window.BT && BT.brandName ? BT.brandName(`${st.name} - ${fmt(W/d)}x${fmt(H/d)}in.png`) : `${st.name} - ${fmt(W/d)}x${fmt(H/d)}in.png`), await pngBlob(cv2,d));
      for(const k of [...cache.keys()]) if(!k.endsWith('|'+PREV)) cache.delete(k);
    }
    save(await zip.generateAsync({type:'blob'}),'stickers - BA.zip');
    const props=dlProps('zip',{ files:seen.size });
    track('ss_download_click',props); klEvent('Used Sticker Sheet Maker',props);
    toast(`Zipped ${seen.size} sticker${seen.size>1?'s':''}`+(lowered?' (big ones at the most this device can make)':''));
  });
};

/* ---------------- Adjust photo (move + zoom inside the shape) ----------------
   The shape stays fixed in the middle; drag the photo to move it, zoom with the slider,
   the mouse wheel or two fingers. The rest of the photo shows dimmed around the shape. */
const CR={ st:null, src:null, shape:'rounded', crop:null, pts:new Map(), pinch:null };
function openCrop(stId){
  const st=S.stickers.get(stId); if(!st||!st.photo) return;
  const src=S.sources.find(x=>x.id===st.srcId); if(!src) return;
  const b=cropBox(src,st.photo);
  Object.assign(CR,{ st, src, shape:st.photo, crop:{zoom:b.zoom,cx:b.cx,cy:b.cy}, pts:new Map(), pinch:null });
  $('#ss-crop').hidden=false; document.documentElement.classList.add('ss-svg-lock');
  $('#ss-crZoom').value=CR.crop.zoom; crShapes(); requestAnimationFrame(crDraw);
  track('ss_photo_adjust_open',{ shape:CR.shape });
}
function closeCrop(){ $('#ss-crop').hidden=true; document.documentElement.classList.remove('ss-svg-lock'); CR.st=null; }
function crShapes(){ $('#ss-crShapes').innerHTML=SHAPES.map(([k,n])=>`<button type="button" data-crshape="${k}" title="${n}" aria-label="${n}" aria-pressed="${CR.shape===k}"><svg viewBox="0 0 24 24" aria-hidden="true">${SHAPE_ICON[k]}</svg></button>`).join(''); }
function crGeom(){
  const cv=$('#ss-crCv'), r=cv.parentNode.getBoundingClientRect(), b=cropBox(CR.src,CR.shape,CR.crop);
  const pad=Math.max(24,Math.min(r.width,r.height)*0.12), k=Math.min((r.width-pad*2)/b.w,(r.height-pad*2)/b.h);
  const fw=b.w*k, fh=b.h*k, fx=(r.width-fw)/2, fy=(r.height-fh)/2;
  return { r, b, k, fx, fy, fw, fh };
}
function crDraw(){
  if(!CR.st) return;
  const cv=$('#ss-crCv'), g=crGeom(), dpr=window.devicePixelRatio||1;
  cv.width=Math.round(g.r.width*dpr); cv.height=Math.round(g.r.height*dpr);
  const x=cv.getContext('2d'); x.setTransform(dpr,0,0,dpr,0,0); x.clearRect(0,0,g.r.width,g.r.height);
  const ix=g.fx-g.b.x*g.k, iy=g.fy-g.b.y*g.k, iw=CR.src.w*g.k, ih=CR.src.h*g.k;
  x.imageSmoothingQuality='high';
  x.globalAlpha=0.35; x.drawImage(CR.src.canvas,ix,iy,iw,ih); x.globalAlpha=1;
  const p=shapePath(CR.shape,g.fw,g.fh);
  x.save(); x.translate(g.fx,g.fy); x.clip(p); x.translate(-g.fx,-g.fy); x.drawImage(CR.src.canvas,ix,iy,iw,ih); x.restore();
  x.save(); x.translate(g.fx,g.fy); x.lineWidth=2; x.strokeStyle='#fff'; x.stroke(p); x.setLineDash([6,5]); x.strokeStyle=ACC; x.stroke(p); x.restore();
}
function crMove(dx,dy){ const g=crGeom(); CR.crop.cx-=dx/g.k/CR.src.w; CR.crop.cy-=dy/g.k/CR.src.h; const b=cropBox(CR.src,CR.shape,CR.crop); CR.crop.cx=b.cx; CR.crop.cy=b.cy; crDraw(); }
function crZoom(z){ CR.crop.zoom=clamp(z,1,5); const b=cropBox(CR.src,CR.shape,CR.crop); CR.crop.cx=b.cx; CR.crop.cy=b.cy; $('#ss-crZoom').value=CR.crop.zoom; crDraw(); }
function applyCrop(){
  const st=CR.st, src=CR.src; if(!st) return closeCrop();
  const oldLong=new Map(); S.sheets.forEach(sh=>sh.items.forEach(it=>{ if(it.stickerId===st.id) oldLong.set(it,Math.max(it.widthIn,it.widthIn*st.h/st.w)); }));
  src.crop={...CR.crop}; const nst=makePhoto(src,CR.shape); photoShape=CR.shape;
  for(const [it,L] of oldLong) it.widthIn=nst.w>=nst.h?L:L*nst.w/nst.h;
  track('ss_photo_adjust',{ shape:CR.shape, zoom:Math.round(CR.crop.zoom*100)/100 });
  closeCrop(); renderLib(); refresh(); try{ vecOf(nst); }catch(e){}
}
{
  const stage=$('#ss-crStage');
  if(stage){
    stage.addEventListener('pointerdown',e=>{ stage.setPointerCapture(e.pointerId); CR.pts.set(e.pointerId,[e.clientX,e.clientY]);
      if(CR.pts.size===2){ const [a,b]=[...CR.pts.values()]; CR.pinch={ d:Math.hypot(a[0]-b[0],a[1]-b[1]), z:CR.crop.zoom }; } });
    stage.addEventListener('pointermove',e=>{ const prev=CR.pts.get(e.pointerId); if(!prev) return;
      CR.pts.set(e.pointerId,[e.clientX,e.clientY]);
      if(CR.pts.size>=2&&CR.pinch){ const [a,b]=[...CR.pts.values()]; crZoom(CR.pinch.z*Math.hypot(a[0]-b[0],a[1]-b[1])/Math.max(1,CR.pinch.d)); return; }
      crMove(e.clientX-prev[0],e.clientY-prev[1]); });
    const up=e=>{ CR.pts.delete(e.pointerId); if(CR.pts.size<2) CR.pinch=null; };
    stage.addEventListener('pointerup',up); stage.addEventListener('pointercancel',up);
    stage.addEventListener('wheel',e=>{ e.preventDefault(); crZoom(CR.crop.zoom*Math.exp(-e.deltaY*0.0015)); },{passive:false});
    $('#ss-crZoom').addEventListener('input',e=>crZoom(parseFloat(e.target.value)));
    $('#ss-crShapes').addEventListener('click',e=>{ const b=e.target.closest('[data-crshape]'); if(!b) return; CR.shape=b.dataset.crshape; const bx=cropBox(CR.src,CR.shape,CR.crop); CR.crop.cx=bx.cx; CR.crop.cy=bx.cy; crShapes(); crDraw(); });
    $('#ss-crReset').onclick=()=>{ CR.crop={zoom:1,cx:0.5,cy:0.5}; $('#ss-crZoom').value=1; crDraw(); };
    $('#ss-crCancel').onclick=closeCrop;
    $('#ss-crApply').onclick=applyCrop;
    $('#ss-crop').addEventListener('click',e=>{ if(e.target.id==='ss-crop') closeCrop(); });
    document.addEventListener('keydown',e=>{ if($('#ss-crop').hidden) return; if(e.key==='Escape') closeCrop(); if(e.key==='Enter') applyCrop(); });
    new ResizeObserver(()=>crDraw()).observe(stage);
  }
}

/* ---------------- free designs (SVG Library data in Shopify Files) ----------------
   Bare picker: search + thumbnails. Same data as assets/svg-library.js: svglib-index.json lists
   every cut file with [pack, offset, length] for its thumbnail (t) and SVG (s); each is read
   with a range request. SVG packs (.bin) are stored scrambled and unscrambled per file here.
   Cut templates (boxes, cards, envelopes…) are left out: they make poor stickers. */
const SVG={ el:$('#ss-svg'), url:'', packs:null, files:null, shown:48, q:'', loadP:null, io:null, busy:false };
SVG.url=SVG.el?SVG.el.getAttribute('data-ss-svgindex')||'':'';
const SVG_SKIP=/\b(3d|box|boxes|card|cards|envelope|envelopes|template|templates|holder|base|gable|bag|net|lookbook|topper|toppers|shaker|lantern|centerpiece|dome|domes)\b/i;
const SVG_KEY="56cc146efa78ea9d6ea82ba555272cb5";
function svgFnv(str){ let h=2166136261; for(let i=0;i<str.length;i++){ h^=str.charCodeAt(i); h=Math.imul(h,16777619)>>>0; } return h||1; }
function svgUnscramble(bytes,id){ let x=svgFnv(SVG_KEY+':'+id); const out=new Uint8Array(bytes.length);
  for(let i=0;i<bytes.length;i++){ x^=(x<<13)>>>0; x>>>=0; x^=x>>>17; x^=(x<<5)>>>0; x>>>=0; out[i]=bytes[i]^(x&255); } return out; }
const svgTitle=t=>String(t||'').replace(/\s+-\s+design team.*$/i,'').replace(/\b\w/g,c=>c.toUpperCase());
function svgLoad(){
  if(!SVG.loadP) SVG.loadP=(async()=>{
    if(!SVG.url) throw new Error('no index');
    let idx=null;
    for(let t=0;t<3&&!idx;t++){ try{ const r=await fetch(SVG.url); if(r.ok) idx=await r.json(); }catch(e){} if(!idx) await new Promise(r=>setTimeout(r,700*(t+1))); }
    if(!idx||!Array.isArray(idx.files)) throw new Error('index');
    const base=new URL('.',new URL(SVG.url,location.href)).href;
    SVG.packs=idx.packs.map(([n,v])=>`${base}${n}?v=${v}`);
    SVG.files=idx.files.filter(f=>f.s&&f.t&&!SVG_SKIP.test(f.label+' '+f.design)).map(f=>{
      const part=(/\(([^)]+)\)\s*$/.exec(f.label)||[])[1]||'';
      return { id:f.id, sku:f.sku, name:svgTitle(f.design||f.label), part, s:f.s, t:f.t,
        hay:[f.design,f.label,f.category,(f.tags||[]).join(' ')].join(' ').toLowerCase() };
    }).sort((a,b)=>a.name.localeCompare(b.name));
  })().catch(e=>{ SVG.loadP=null; throw e; });
  return SVG.loadP;
}
const svgRange=new Map();
function svgRead(loc){
  const k=loc.join(':');
  if(!svgRange.has(k)) svgRange.set(k,(async()=>{
    const [p,off,len]=loc, u=SVG.packs[p]; let err;
    for(let t=0;t<3;t++){
      try{
        const r=await fetch(u,{ headers:{ Range:`bytes=${off}-${off+len-1}` } });
        if(r.status===206){ const b=new Uint8Array(await r.arrayBuffer()); if(b.length===len) return b; err=new Error('short'); }
        else if(r.ok){ return new Uint8Array(await r.arrayBuffer()).slice(off,off+len); }
        else err=new Error('range '+r.status);
      }catch(e){ err=e; }
      await new Promise(r=>setTimeout(r,400*(t+1)));
    }
    throw err;
  })().catch(e=>{ svgRange.delete(k); throw e; }));
  return svgRange.get(k);
}
function svgThumb(img){
  const f=SVG.files.find(x=>x.id===img.dataset.id); if(!f||img.dataset.done) return; img.dataset.done='1';
  svgRead(f.t).then(b=>{ img.src=URL.createObjectURL(new Blob([b],{type:'image/webp'})); }).catch(()=>{ delete img.dataset.done; });
}
function svgRender(){
  const grid=$('#ss-svgGrid'), more=$('#ss-svgMore'), q=SVG.q.trim().toLowerCase();
  const words=q.split(/\s+/).filter(Boolean);
  const list=words.length?SVG.files.filter(f=>words.every(w=>f.hay.includes(w))):SVG.files;
  if(SVG.io) SVG.io.disconnect();
  SVG.io='IntersectionObserver' in window?new IntersectionObserver(es=>es.forEach(e=>{ if(e.isIntersecting){ svgThumb(e.target); SVG.io.unobserve(e.target); } }),{ root:grid, rootMargin:'300px' }):null;
  grid.innerHTML=list.slice(0,SVG.shown).map(f=>`<button type="button" class="svg__it" data-id="${f.id}" title="${(f.name+(f.part?' — '+f.part:'')).replace(/"/g,'&quot;')}"><img alt="" data-id="${f.id}"><span>${f.name.replace(/</g,'&lt;')}</span></button>`).join('')
    || `<p class="ss-hint svg__none">No designs match “${SVG.q.replace(/</g,'&lt;')}”.</p>`;
  grid.querySelectorAll('img[data-id]').forEach(im=>SVG.io?SVG.io.observe(im):svgThumb(im));
  more.hidden=list.length<=SVG.shown;
  $('#ss-svgCount').textContent=`${list.length} design${list.length===1?'':'s'}`;
}
async function svgOpen(){
  if(!SVG.el) return;
  SVG.el.hidden=false; document.documentElement.classList.add('ss-svg-lock');
  track('ss_library_open',{});
  if(SVG.files){ svgRender(); return; }
  $('#ss-svgGrid').innerHTML='<p class="ss-hint svg__none">Loading free designs…</p>';
  try{ await svgLoad(); svgRender(); }
  catch(e){ console.error('[Sticker Sheet Maker] library',e); $('#ss-svgGrid').innerHTML='<p class="ss-hint svg__none">Couldn’t load the designs. Check your connection and try again.</p>'; }
}
function svgClose(){ if(!SVG.el) return; SVG.el.hidden=true; document.documentElement.classList.remove('ss-svg-lock'); }
/* SVG → transparent PNG File (longest side 1600px), then the normal upload path. */
async function svgPick(f){
  if(SVG.busy) return; SVG.busy=true; svgClose(); toast(`Loading ${f.name}…`,60000);
  try{
    const raw=await svgRead(f.s);
    const text=new TextDecoder().decode(/\.bin(\?|$)/.test(SVG.packs[f.s[0]])?svgUnscramble(raw,f.id):raw);
    const doc=new DOMParser().parseFromString(text,'image/svg+xml'), el=doc.documentElement;
    if(!el||el.nodeName.toLowerCase()!=='svg') throw new Error('bad svg');
    const num=v=>parseFloat(String(v||'').replace(/px$/,''));
    let vb=(el.getAttribute('viewBox')||'').trim().split(/[\s,]+/).map(Number);
    if(vb.length!==4||!(vb[2]>0&&vb[3]>0)){ const w=num(el.getAttribute('width'))||1000, h=num(el.getAttribute('height'))||1000; vb=[0,0,w,h]; el.setAttribute('viewBox',vb.join(' ')); }
    const k=1600/Math.max(vb[2],vb[3]), W=Math.round(vb[2]*k), H=Math.round(vb[3]*k);
    el.setAttribute('width',W); el.setAttribute('height',H); el.setAttribute('preserveAspectRatio','xMidYMid meet');
    const url=URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(el)],{type:'image/svg+xml'}));
    const img=await new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=url; });
    const c=mk(W,H); c.getContext('2d').drawImage(img,0,0,W,H); URL.revokeObjectURL(url);
    const blob=await new Promise(r=>c.toBlob(r,'image/png'));
    track('ss_library_pick',{ svg_sku:f.sku, svg_file:f.id });
    await addFiles([new File([blob],(f.part?`${f.name} ${f.part}`:f.name)+'.png',{type:'image/png'})],'library');
  }catch(e){ console.error('[Sticker Sheet Maker] library pick',e); toast('Couldn’t load that design. Try again or pick another.'); }
  SVG.busy=false;
}
if(SVG.el){
  if(!SVG.url) document.querySelectorAll('[data-ss-svgopen]').forEach(b=>b.hidden=true);
  document.querySelectorAll('[data-ss-svgopen]').forEach(b=>b.addEventListener('click',e=>{ e.stopPropagation(); svgOpen(); }));
  $('#ss-svgClose').onclick=svgClose;
  SVG.el.addEventListener('click',e=>{ if(e.target===SVG.el) return svgClose(); const it=e.target.closest('.svg__it'); if(it){ const f=SVG.files&&SVG.files.find(x=>x.id===it.dataset.id); if(f) svgPick(f); } });
  let qT; $('#ss-svgQ').addEventListener('input',e=>{ clearTimeout(qT); qT=setTimeout(()=>{ SVG.q=e.target.value; SVG.shown=48; if(SVG.files){ svgRender(); $('#ss-svgGrid').scrollTop=0; } },150); });
  $('#ss-svgQ').addEventListener('keydown',e=>{ if(e.key==='Enter') e.target.blur(); });
  $('#ss-svgMore').onclick=()=>{ SVG.shown+=48; svgRender(); };
  document.addEventListener('keydown',e=>{ if(e.key==='Escape'&&!SVG.el.hidden&&!document.querySelector('.bt-proc:not([hidden])')) svgClose(); });
}

/* ---------------- controls ---------------- */
const drop=$('#ss-drop'), file=$('#ss-file');
$('#ss-pick').onclick=e=>{ e.stopPropagation(); file.click(); };
$('#ss-more').onclick=()=>file.click();
/* sample stickers: fetched from Shopify Files (same origin), then the normal upload path */
const sampleBtn=$('#ss-samples');
let samples=[]; try{ samples=JSON.parse(($('#ss-sample-data')||{}).textContent||'[]')||[]; }catch(e){}
function loadSampleFiles(){
  return Promise.all(samples.map(async s=>{
    const r=await fetch(s.url,{ headers:{ Accept:'image/webp,image/png,image/*' } }); if(!r.ok) throw new Error(r.status);
    const b=await r.blob(); return new File([b], s.name+'.'+((b.type.split('/')[1]||'png').replace('jpeg','jpg')), { type:b.type||'image/png' });
  }));
}
if(sampleBtn){
  if(!samples.length) sampleBtn.hidden=true;
  sampleBtn.onclick=async e=>{
    e.stopPropagation(); if(sampleBtn.disabled) return;
    sampleBtn.disabled=true; toast('Loading sample stickers…',60000);
    try{ await addFiles(await loadSampleFiles(),'sample'); }
    catch(err){ console.error('[Sticker Sheet Maker] samples',err); toast('Couldn’t load the samples. Check your connection and try again.'); }
    sampleBtn.disabled=false;
  };
}

/* ---------------- sample sheet on load ----------------
   First visit: the sheet opens already made — the sample stickers placed and the page filled with
   copies — so a visitor sees the result before being asked for anything. Everything on it is a normal
   sticker (move, resize, split, download through the usual popup). It steps aside the moment they
   bring their own images. */
const demoBar=$('#ss-demoBar');
function demoOn(){ return samples.length>0 && !/[?&]demo=0\b/.test(location.search) && (/[?&]demo=1\b/.test(location.search) || !seenBefore()); }
function seenBefore(){ try{ return !!localStorage.getItem('ss_seen'); }catch(e){ return false; } }
function markSeen(){ try{ localStorage.setItem('ss_seen','1'); }catch(e){} }
async function startDemo(){
  S.demo=true; drop.hidden=true; const t0=Date.now();
  toast('Setting up a sample sheet…',20000);
  try{
    const files=await loadSampleFiles(); if(!S.demo) return;
    const placed=await addFiles(files,'demo'); if(!S.demo) return;
    if(!placed) throw new Error('no stickers');
    const copies=await fillCopies(true); if(!S.demo) return;
    S.sel=null; refresh(); demoBar.hidden=false; $('#ss-toast').classList.remove('is-on');
    track('ss_demo_loaded',{ stickers:S.stickers.size, on_sheet:sheet().items.length, copies, ms:Date.now()-t0 });
  }catch(err){
    console.error('[Sticker Sheet Maker] sample sheet',err);
    if(S.demo){ S.demo=false; for(const s of S.sources.filter(x=>x.demo)) removeDemoSource(s); undoStack.length=0; }
    $('#ss-toast').classList.remove('is-on'); renderSources(); renderLib(); refresh();
  }
}
function removeDemoSource(s){
  const ids=new Set([...S.stickers.values()].filter(x=>x.srcId===s.id).map(x=>x.id));
  S.sources.splice(S.sources.indexOf(s),1); ids.forEach(id=>S.stickers.delete(id));
  S.sheets.forEach(sh=>sh.items=sh.items.filter(i=>!ids.has(i.stickerId)));
  for(const k of [...cache.keys()]) if(ids.has(+k.split('|')[0])) cache.delete(k);
}
/* drop the sample sheet: own upload / library pick / Clear sheet / "Use my own images" */
function exitDemo(how){
  if(!S.demo) return; S.demo=false;
  for(const s of S.sources.filter(x=>x.demo)) removeDemoSource(s);
  undoStack.length=0; S.sel=null; demoBar.hidden=true; markSeen();
  renderSources(); renderLib(); refresh();
  track('ss_demo_replaced',{ how });
}
if(demoBar){
  $('#ss-demoDl').onclick=()=>{ track('ss_demo_download_click',{ on_sheet:sheet().items.length }); $('#ss-dlOne').click(); };
  $('#ss-demoOwn').onclick=()=>{ exitDemo('own'); file.click(); };
}
file.onchange=()=>{ addFiles(file.files); file.value=''; };
// drop images anywhere on the tool (canvas or steps); the big dropzone highlights while dragging
const hasFiles=e=>e.dataTransfer&&[...(e.dataTransfer.types||[])].includes('Files');
ROOT.addEventListener('dragover',e=>{ if(!hasFiles(e)) return; e.preventDefault(); drop.classList.add('is-over'); });
ROOT.addEventListener('dragleave',e=>{ if(!ROOT.contains(e.relatedTarget)) drop.classList.remove('is-over'); });
ROOT.addEventListener('drop',e=>{ if(!hasFiles(e)) return; e.preventDefault(); drop.classList.remove('is-over'); addFiles(e.dataTransfer.files); });
document.addEventListener('paste',e=>{ if(/INPUT|TEXTAREA/.test(document.activeElement?.tagName)) return; const f=[...(e.clipboardData?.files||[])].filter(x=>x.type.startsWith('image/')); if(f.length) addFiles(f); });

const bindRange=(id,key,lbl,fmtFn)=>{ const i=$(id); i.addEventListener('input',()=>{ S.det[key]=parseFloat(i.value); $(lbl).textContent=fmtFn(S.det[key]); redetect(); }); };
bindRange('#ss-sens','sens','#ss-sensV',v=>v===1?'Auto':(v<1?'Less':'More')+` (${Math.round(v*100)}%)`);
bindRange('#ss-big','big','#ss-bigV',v=>fmt(v)+' in');
$('#ss-big').addEventListener('input',()=>{ S.det.bigSet=true; });   // their own size wins over fitting a packed sheet on the page
/* Background: Remove / Keep for every uploaded (non-transparent) image at once */
document.querySelectorAll('#ss-bgMode [data-bg]').forEach(b=>b.onclick=async()=>{
  const keep=b.dataset.bg==='keep', list=S.sources.filter(x=>x.opaque&&x.photo!==keep);
  if(!list.length) return;
  track('ss_bg_mode',{ mode:keep?'keep':'remove', images:list.length });
  for(const src of list) await switchKind(src,keep);
});

/* Place stickers (ids) where they fit, biggest first. Used right after an upload ('auto') and by Add all. */
async function placeStickers(ids, how){
  const quiet=how==='demo';
  if(!quiet){ snapshot(); toast('Placing stickers…',30000); } await tick();
  const fresh=ids.map(id=>S.stickers.get(id)).filter(Boolean).map(st=>({id:nid(), stickerId:st.id, x:0, y:0, widthIn:Math.min(st.natW,S.sheetW-2*S.margin), rot:0, offset:null}));
  // size every sticker (outline + offset) one at a time so the page never locks up
  const area=new Map();
  for(const [i,it] of fresh.entries()){ area.set(it,areaOf(it)); if(fresh.length>4){ if(!quiet) toast(`Preparing stickers… ${i+1} of ${fresh.length}`,30000); await tick(); } }
  fresh.sort((a,b)=>area.get(b)-area.get(a));
  const b=makeBoard(); for(const o of sheet().items) stamp(b,o);
  let n=0; for(const [i,it] of fresh.entries()){ if(place(b,it,[0])){ sheet().items.push(it); n++; } if(fresh.length>4){ if(!quiet) toast(`Placing stickers… ${i+1} of ${fresh.length}`,30000); await tick(); } }
  const left=fresh.length-n;
  S.sel=null; refresh();
  if(quiet) return n;
  if(n) notePlaced(how);
  toast(`Placed ${n} sticker${n===1?'':'s'}`+(left?` — ${left} didn't fit. Shrink some or clear space, then tap them in step 1.`:''),left?6000:2600);
}
$('#ss-addAll').onclick=()=>placeStickers([...S.stickers.keys()],'add_all');
/* Phones (kit app mode): an upload pops step 1 open over the sheet; close it so the placed stickers show. */
function closeAppPanel(){ const r=ROOT.closest('.bt[data-app]'); if(!r) return; setTimeout(()=>{ const x=r.querySelector('.bt-app-close'); if(x&&r.hasAttribute('data-app')) x.click(); },80); }
const modeHints={cricut:'Print-and-cut: your cutting machine cuts around the outer edge of each sticker and its offset, so no outline is needed.',
                 paper:'A thin outline is printed around each offset so you can cut by hand.'};
function setMode(m){ S.mode=m; S.lineOn=m==='paper'; $('#ss-lineOn').checked=S.lineOn;
  // Print Then Cut: lock to 300 DPI (Cricut's recommended print resolution; Design Space prints what it's given)
  const d=$('#ss-dpi'); if(m==='cricut'){ d.value='300'; S.dpi=300; d.disabled=true; d.title='Locked to 300 DPI for print-and-cut'; } else { d.disabled=false; d.title=''; }
  sizeHint(); $('#ss-paperOpts').hidden=m!=='paper'; $('#ss-modeHint').textContent=modeHints[m]; cache.clear(); draw(); }
$('#ss-off').oninput=e=>{ S.offset=parseFloat(e.target.value); $('#ss-offV').textContent=fmt(S.offset)+' in'; draw(); syncSel(); };
$('#ss-sm').oninput=e=>{ S.smooth=parseFloat(e.target.value); $('#ss-smV').textContent=fmt(S.smooth)+' in'; draw(); };
$('#ss-offColor').oninput=e=>{ S.offColor=e.target.value; cache.clear(); draw(); };
$('#ss-lineOn').onchange=e=>{ S.lineOn=e.target.checked; draw(); };
$('#ss-lineW').oninput=e=>{ const v=parseFloat(e.target.value); if(isFinite(v)&&v>0){ S.lineW=v; draw(); } };
$('#ss-lineC').oninput=e=>{ S.lineC=e.target.value; draw(); };
$('#ss-whiteBg').onchange=e=>{ S.whiteBg=e.target.checked; draw(); };
function setSheet(){
  const p=$('#ss-preset').value, wasHidden=$('#ss-customRow').hidden; $('#ss-customRow').hidden=p!=='custom';
  if(p==='custom'&&wasHidden) toast('Set the custom width and height in step 2',4000);
  setMode(p==='7.44x9.94'?'cricut':'paper');
  if(p!=='custom'){ const [w,h]=p.split('x').map(Number); S.sheetW=w; S.sheetH=h; $('#ss-sw').value=w; $('#ss-sh').value=h; }
  else { S.sheetW=Math.max(1,parseFloat($('#ss-sw').value)||6.75); S.sheetH=Math.max(1,parseFloat($('#ss-sh').value)||9.25); }
  fit(); refresh();
}
$('#ss-preset').onchange=setSheet; $('#ss-sw').oninput=setSheet; $('#ss-sh').oninput=setSheet;
$('#ss-margin').oninput=e=>{ S.margin=Math.max(0,parseFloat(e.target.value)||0); draw(); };
$('#ss-allowRot').onchange=e=>S.allowRot=e.target.checked;
$('#ss-fillBtn').onclick=()=>fillCopies(false);
$('#ss-dpi').onchange=e=>S.dpi=parseInt(e.target.value,10);
$('#ss-trim').onchange=e=>{ S.trim=e.target.checked; sizeHint(); };
$('#ss-undo').onclick=undo; $('#ss-fit').onclick=fit;
$('#ss-clearSheet').onclick=()=>{ if(S.demo) return exitDemo('clear'); if(!sheet().items.length) return; snapshot(); sheet().items=[]; S.sel=null; refresh(); };

setMode('paper'); resize(); refresh(); renderSources();
if(!window.ClipperLib) toast('Smooth offsets didn’t load, so a simpler offset is used. Refresh the page to try again.');
if(demoBar&&demoOn()) startDemo();
if(/[?&]debug_mode=1\b/.test(location.search)) window.__SS=S; // for testing only
})();